/**
 * OrderflowBot.js — market-maker model
 * ─────────────────────────────────────────────────────────────
 * Model: balance → imbalance. Markets oscillate between two-sided
 * auction (balance, churn, inventory transfer) and one-sided drive
 * (imbalance, gap, acceptance away from value). The edge claim is
 * that gaps OUT of a mature base have a higher base rate of continuing
 * than gaps into thin structure, because the base trapped inventory
 * on both sides during the comfortable phase.
 *
 * State machine:
 *   SCANNING  — watching for a base to mature
 *   PILOT     — starter position open; inventory held because conditions
 *               qualify, NOT because we predict direction
 *   LOADED    — imbalance fired; direction now known; full size deployed
 *               (starter + load), management based on conditions persistence
 *   COOLDOWN  — post-exit pause until conditions are fresh
 *
 * Filter is the edge: a base qualifies only if churn matured (duration +
 * peak), inventory absorbed (volume), and a liquidity gap exists. The
 * pilot is dumb (fixed % stop, time cap). The load is earned (real vol
 * expansion with directional bias). Management is condition-based, not
 * price-based — we exit when the REASONS die, not when price moves.
 *
 * Long-only in V1. The model is direction-agnostic in principle, but
 * pilot shorts require short capability the trading surface doesn't
 * always expose. Set `pilotDirection: 'SHORT'` when ready.
 * ─────────────────────────────────────────────────────────────
 */

// Exported state names — UI components import these for state comparison
// Build stamp — bot_runner prints it at boot so the droplet log shows which
// OrderflowBot is live (the droplet runs its OWN copy of this file).
export const BOT_BUILD = '2026-10-23-loosen'

// One-time saved-config migration (bot-loosen-20261023). Saved configs
// (localStorage solcheddar_bot_config / runner state) override defaults, so
// loosened defaults would never reach an existing user. Bumping CFG_VER forces
// ONLY these keys to the new values once; every other saved setting is kept.
export const CFG_VER = '2026-10-23-loosen'
export const CFG_MIGRATION = {
  ctxLiqBaseMin: 30, ctxLiqMaxGapSec: 90, ctxLiqBuyMin: 0.42,
  ctxValueTol: 0.35,
  ctxAbsorbMult: 2, ctxAbsorbSec: 300,
  ctxTrendLH: 5,
  maxExtAtr: 2.5, maxUpBars: 6,
}
export function migrateBotConfig(cfg) {
  if (!cfg || typeof cfg !== 'object') return cfg
  if (cfg.cfgVer === CFG_VER) return cfg
  return { ...cfg, ...CFG_MIGRATION, cfgVer: CFG_VER }
}

export const STATES = {
  SCANNING: 'SCANNING',
  PILOT:    'PILOT',
  LOADED:   'LOADED',
  COOLDOWN: 'COOLDOWN',
}

// Legacy exports — retained so old imports don't break during migration.
// Not used by the bot internally. Safe to delete once nothing references them.
export const ENTRY_MODES = {}
export const HOLD_STAGES = {}
export const DIRECTIONS  = { LONG: 'LONG', SHORT: 'SHORT' }

// ─── Internal filter constants — the edge, NOT user-tunable ─────────
// Loosening any of these turns the strategy into "small bag and hope."
// Adjustments belong in code review, not config.
const BASE_OPEN_CHURN        = 4.0     // churn must hit this to open a base
const BASE_DEAD_CHURN        = 2.0     // churn this low for grace period = base dies
const BASE_CLOSE_GRACE_MS    = 30_000  // 30s of dead churn before base closes
const BASE_MIN_DURATION_MS   = 180_000 // 3min minimum churn duration to qualify
const BASE_MIN_PEAK_CHURN    = 5.0     // base must have peaked this high
const BASE_MIN_ABSORPTION    = 2.0     // base totalVol / (baselineRate × duration)
const LOAD_WINDOW_SEC        = 8       // window for vol-spike imbalance detection
const LOAD_VOL_SPIKE_MULT    = 2.5     // winRate / baseRate to count as imbalance
const LOAD_DELTA_THRESH      = 0.30    // |buy-sell|/total must exceed this
const LOAD_HEALTH_WINDOW_SEC = 10      // tape window for thesis-health read
const LOAD_HEALTH_THRESHOLD  = 0.30    // health below this = thesis degrading
const LOAD_HEALTH_PERSIST_MS = 30_000  // sustained at threshold for this long = exit
const WALL_BOX_BOTH_SIDES_PCT = 0.04   // walls within this on both sides = boxed in
const LOAD_STOP_STRUCT_BUFFER = 0.005  // load stop sits 0.5% past the trigger's structural extreme

// ── Load liquidity/volatility gate (mirrors the ARD Discord filter) ──
// Journal 67-trade read: loaded losers averaged -8.6R size-weighted, and the
// worst of them (-18.8R, -31.8R) were thin-book dumps. The gate requires the
// load to fire AT a high-volume node on a book that actually moves.
const LOAD_GATE_WINDOW_SEC = 120     // profile + volatility window
const LOAD_NODE_STEP_PCT   = 0.002   // price-bin width (0.2% of price) — same as ArdDetector
const LOAD_NODE_BAND_PCT   = 0.006   // "at a node" = within 0.6% — same as ArdDetector
const LOAD_NODE_TOP_FRAC   = 0.25    // top 25% of bins by volume are "value nodes"

// ── Rising-imbalance entry trigger (primary LOAD trigger) ──────────
// A level is sell-imbalanced when sellVol dominates buyVol by IMB_RATIO
// (same-level rule, matches IMBALANCE_RATIO in chartUtils — the red numbers
// on the chart). The candle's structural low is the bottom of its LOWEST run
// of >= IMB_STACK_MIN consecutive imbalanced levels that carries a meaningful
// share of the candle's volume. Wicks carry no volume, so they never qualify —
// the structural low sits above the wick by construction.
const IMB_RATIO          = 5      // MUST match IMBALANCE_RATIO in chartUtils
const IMB_STACK_MIN      = 2      // consecutive imbalanced levels = a stack
const IMB_STACK_VOL_FRAC = 0.03   // stack vol must be >= this share of candle vol
const IMB_LOOKBACK       = 6      // completed candles scanned for the structure
const IMB_RISING_MIN     = 3      // qualifying candles that must strictly progress

// Lowest sell-imbalance stack low (side='sell') or highest buy-imbalance stack
// high (side='buy') for a single candle. Returns null if no qualifying stack.
// "Stack" = a run of consecutive POPULATED levels that are all imbalanced.
function imbalanceStackExtreme(candle, side) {
  const levels = candle && candle.mcapLevels
  if (!levels) return null
  const rows = Object.keys(levels).map(k => {
    const lvl = levels[k] || {}
    return { mcap: Number(k), buy: lvl.buy || 0, sell: lvl.sell || 0 }
  }).filter(r => Number.isFinite(r.mcap)).sort((a, b) => a.mcap - b.mcap)
  if (!rows.length) return null

  let candleVol = 0
  for (const r of rows) candleVol += r.buy + r.sell
  if (candleVol <= 0) return null

  const isImb = (r) => {
    const dom = side === 'sell' ? r.sell : r.buy
    const oth = side === 'sell' ? r.buy : r.sell
    if (dom <= 0) return false
    if (oth <= 0) return true              // one-sided level = imbalance (matches chart)
    return dom / oth >= IMB_RATIO
  }

  // Collect maximal runs of consecutive imbalanced levels.
  const stacks = []
  let run = null
  for (const r of rows) {
    if (isImb(r)) {
      if (!run) run = { lo: r.mcap, hi: r.mcap, vol: 0, len: 0 }
      run.hi = r.mcap; run.len += 1
      run.vol += (side === 'sell' ? r.sell : r.buy)
    } else if (run) { stacks.push(run); run = null }
  }
  if (run) stacks.push(run)

  const ok = stacks.filter(s => s.len >= IMB_STACK_MIN && s.vol >= candleVol * IMB_STACK_VOL_FRAC)
  if (!ok.length) return null

  if (side === 'sell') {
    let best = ok[0]
    for (const s of ok) if (s.lo < best.lo) best = s
    return best.lo                          // lowest sell-imbalance floor
  }
  let best = ok[0]
  for (const s of ok) if (s.hi > best.hi) best = s
  return best.hi                            // highest buy-imbalance ceiling
}

export class OrderflowBot {
  constructor(config = {}) {
    this.state = STATES.SCANNING
    this.enabled = false

    // ─── Config (user-facing surface) ───
    this.config = {
      // Position sizing
      starterSizeSol:    config.starterSizeSol    ?? 0.05,   // pilot/starter size
      loadSizeSol:       config.loadSizeSol       ?? 0.30,   // added on load (max — see loadRiskCapR)

      // ── Risk-neutral loading ──
      // Total SOL at risk after a load (each leg losing its own % to the load
      // stop) may not exceed loadRiskCapR × the pilot's SOL risk budget
      // (starterSizeSol × pilotStopPct). The stop stays STRUCTURAL — size is
      // the free variable: the add is scaled down to what the structure
      // affords, and skipped entirely if even a dust add doesn't fit.
      // 1.0 = a failed load costs what a failed pilot costs.
      loadRiskCapR:      config.loadRiskCapR      ?? 1.0,
      // ── Load liquidity/volatility gate ──
      // Same conditions as the ARD Discord filter: price at a top-N volume
      // node, real range in the window, enough tape to read at all.
      loadGateEnabled:   config.loadGateEnabled   ?? true,
      loadMinRangePct:   config.loadMinRangePct   ?? 5.0,    // window range % floor
      loadTopNodes:      config.loadTopNodes      ?? 3,      // node rank must be ≤ this
      loadGateMinSwaps:  config.loadGateMinSwaps  ?? 30,     // else: thin tape, block

      // Pilot management
      pilotStopPct:      config.pilotStopPct      ?? 0.04,   // % used by stop mode 'pct'
      pilotMaxHoldMin:   config.pilotMaxHoldMin   ?? 20,     // max minutes in PILOT
      pilotHoldMode:     config.pilotHoldMode     ?? 'time', // 'time' | 'off' (no time cap)

      // ── Initial stop (BotSettingsMenu → Pilot → Stop) ──
      // 'base'  : under the base low − stopBufferPct
      // 'swing' : under the last swing low on mgmt bars (1m) − stopBufferPct
      // 'atr'   : entry − levelAtrMult × ATR(atrPeriod) on mgmt bars
      // 'pct'   : legacy entry × (1 − pilotStopPct)
      // An invalid level falls back atr → pct. Stops wider than maxStopPct
      // skip the entry (logged STOP_TOO_WIDE), tighter than minStopPct widen.
      pilotStopMode:     config.pilotStopMode     ?? 'atr',
      levelAtrMult:      config.levelAtrMult      ?? 2.0,    // "Entry stop ×"
      stopBufferPct:     config.stopBufferPct     ?? 0.01,
      minStopPct:        config.minStopPct        ?? 0.01,
      maxStopPct:        config.maxStopPct        ?? 0.25,
      swingStrength:     config.swingStrength     ?? 2,      // bars each side of a pivot low

      // ── Risk-based sizing ──
      // SOL risk per trade = starterSizeSol × riskPct (0.05 × 4% = 0.002, the
      // same budget the fixed 4% stop had). Size = risk / stop distance,
      // clamped to [minSizeSol, maxSizeSol].
      riskPct:           config.riskPct           ?? 0.04,
      minSizeSol:        config.minSizeSol        ?? 0.01,
      maxSizeSol:        config.maxSizeSol        ?? 0.10,
      // Gap reserve (bot-stops-20261017): on fast tapes the first print past
      // a stop is already well beyond it (the log's 4% stops lost -1.38R avg,
      // worst -2.77R). Size against stop distance + the p90 per-print adverse
      // move of the last gapWindowSec, capped at gapReserveMaxPct, so a gapped
      // stop costs ~1R of SOL instead of 2-3R. Stop PLACEMENT is unchanged.
      gapReserve:        config.gapReserve        ?? true,
      gapWindowSec:      config.gapWindowSec      ?? 60,
      gapReserveMaxPct:  config.gapReserveMaxPct  ?? 0.05,

      // ── Entry guards (BotSettingsMenu → Foundations) ──
      // Entry selection is the Foundations block below (entry-gates2-20261018).
      // REMOVED: entryMode (value/flush/off), entryDz flush path, basePosMax /
      // HIGH_IN_BASE, maxAtrAbove / NEAR_VALUE, entryPaths, short-window VWAP.
      // Old saved configs carrying those keys load fine — they are dropped in
      // _normalizeConfig. Two guards stay (null = off):
      maxExtAtr:         config.maxExtAtr         ?? 2.5,    // VWAP on: skip > this many ATR above session VWAP
      cfgVer:            CFG_VER,   // stamped so saved configs skip the migration next load
      maxUpBars:         config.maxUpBars         ?? 6,      // skip after more than this many up 1m closes
      // Bars used for ATR / swing / gate / trail. Bot bars shorter than this
      // are merged up (15s bars → 1m), so ATR is never tick noise.
      mgmtBarSec:        config.mgmtBarSec        ?? 60,

      // ── Entry context — structural foundations (entry-gates-20261018) ──
      // BotSettingsMenu → Foundations, the primary entry logic. ALL ON by default.
      // Read from ctx.candles (bot bars), never rawSwaps (~120s on the droplet).
      // 1 Liquidity: base qualifies from a ctxLiqBaseMin rolling window with
      //   two-sided volume (buy share ≥ ctxLiqBuyMin, no gap > ctxLiqMaxGapSec)
      //   INSTEAD of absorption ≥ 2.0 (absorption is only the fallback when
      //   Liquidity is switched off). Duration/peak-churn rules still apply.
      ctxLiqBase:        config.ctxLiqBase        ?? true,
      ctxLiqBaseMin:     config.ctxLiqBaseMin     ?? 30,
      ctxLiqBuyMin:      config.ctxLiqBuyMin      ?? 0.42,
      ctxLiqMaxGapSec:   config.ctxLiqMaxGapSec   ?? 90,
      // 2 Value: enter only at/below that window's 70% value-area low
      //   + ctxValueTol × VA width. Off = no location check at all.
      ctxValue:          config.ctxValue          ?? true,
      ctxValueTol:       config.ctxValueTol       ?? 0.35,
      // 3 Structure: test low (last 5 min) holds the prior 30-min swing low,
      //   or reclaims it within 60s, and delta since the test low is > 0.
      ctxStructure:      config.ctxStructure      ?? true,
      // 4 Absorption cells: a sell-side absorption cell (chart rule: cell sell
      //   ≥ ctxAbsorbMult × median cell, bar closed above it) at/near the test
      //   low within ctxAbsorbSec, and price reacted up ≥ 0.25 ATR off it.
      ctxAbsorb:         config.ctxAbsorb         ?? true,
      ctxAbsorbSec:      config.ctxAbsorbSec      ?? 300,
      ctxAbsorbMult:     config.ctxAbsorbMult     ?? 2,
      // 5 Trend: veto when ≥ ctxTrendLH of the last 5 comparisons of six 5m
      //   highs are lower highs, or the last-30m POC sits ≥ 2% under the
      //   prior-30m POC (value migrating down).
      ctxTrend:          config.ctxTrend          ?? true,
      ctxTrendLH:        config.ctxTrendLH        ?? 5,
      // 6 VWAP: skip when price is > maxExtAtr ATR above the SESSION VWAP
      //   (every bar seen for the coin since watch start). Off = no VWAP check.
      ctxVwap:           config.ctxVwap           ?? true,
      // Timing: Structure/Absorption read the bot's own bars (15s on the
      //   droplet) incl. the forming bar, instead of completed 1m bars.
      ctxFast:           config.ctxFast           ?? true,

      // ── Market (BotSettingsMenu → Market, 2026-10-18-market) ── default OFF.
      // Data: ctx.market from bot_runner (Jupiter, polled every 60s only while
      // one of these is on). Browser/local mode has no feed → *_NA skips.
      // A Market filter: SOL 1h change ≥ mktSol1hMin % AND SOL 6h change ≥
      //   mktSol6hMin % AND breadth (share of Jupiter trending + top-traded 1h
      //   coins that are up over 1h) ≥ mktBreadthMin. Skip MARKET_WEAK.
      mktFilter:         config.mktFilter         ?? false,
      mktSol1hMin:       config.mktSol1hMin       ?? -0.5,
      mktSol6hMin:       config.mktSol6hMin       ?? -2.0,
      mktBreadthMin:     config.mktBreadthMin     ?? 0.40,
      // B Hot theme: this coin shares a theme (narrative group like cat/dog/
      //   ai/trump, or a name/symbol word) with ≥ mktThemeMin OTHER coins seen
      //   on Jupiter trending/top-traded (1h) in the last 30 min. Skip NO_HOT_THEME.
      mktTheme:          config.mktTheme          ?? false,
      mktThemeMin:       config.mktThemeMin       ?? 2,
      mktMaxAgeSec:      config.mktMaxAgeSec      ?? 300,     // older feed = *_NA

      // ── Management (BotSettingsMenu → Management) ──
      // Profit-lock ladder: [triggerR, lockR] pairs. lockR 0 = breakeven +
      // feeBuffer (exit reason BE_LOCK); lockR > 0 = LOCK_STEP. [] = off.
      lockSteps:         config.lockSteps         ?? [[1, 0], [2, 1]],
      feeBuffer:         config.feeBuffer         ?? 0.01,
      // Scale-out: sell scaleOutPct of the position at +scaleOutR (once).
      scaleOutPct:       config.scaleOutPct       ?? 0.5,
      scaleOutR:         config.scaleOutR         ?? 1.5,
      // ATR trail only arms once peakR ≥ trailStartR (null = from entry).
      trailStartR:       config.trailStartR       ?? 1.0,
      // Runner giveback: in runner mode, exit if price gives back this share
      // of peakR while the ATR trail is not yet above the stop/lock.
      runnerGivebackPct: config.runnerGivebackPct ?? 0.5,
      // ATR trail parameters — the chart indicator's formula (Wilder ATR,
      // HHV of high − mult×ATR). Pilot trail: pilotAtrPeriod null = off.
      pilotAtrPeriod:    config.pilotAtrPeriod    !== undefined ? config.pilotAtrPeriod : 5,
      pilotAtrHhvPeriod: config.pilotAtrHhvPeriod ?? 10,
      pilotAtrMult:      config.pilotAtrMult      ?? 2.0,
      atrHhvPeriod:      config.atrHhvPeriod      ?? 10,

      // Position management — applies to the PILOT and the LOAD alike.
      // These used to live only in _onLoaded, which meant a pilot that never
      // loaded had NO peak tracking, NO trail and NO giveback exit: it could
      // only die by fixed stop, base decay or time cap, none of which are aware
      // of profit. Loads fire ~11% of the time, so 89% of trades were being
      // managed by nothing at all. That is why winners came home flat.
      loadAtrMult:       config.loadAtrMult       ?? 2.0,    // ATR trailing multiplier (loaded)
      loadGivebackR:     config.loadGivebackR     ?? 0.30,   // exit if peakR gives back this fraction
      givebackAfterR:    config.givebackAfterR    ?? 1.0,    // ...but only once peakR clears this
      // Testing kill switch — BotSettingsMenu "Giveback R → off". When false,
      // PEAK_GIVEBACK never fires; ATR trail / hard stop / runner still do.
      // Default OFF since 2026-10-10: the lock ladder + scale-out + trail
      // replace it (it was exiting +1.4R peaks at +0.42R).
      givebackEnabled:   config.givebackEnabled   ?? false,
      // Kill switch. The ATR trail is the one part of this that can make a trade
      // WORSE (it can cut a chop that would have recovered). It's guarded so it
      // can never sit looser than the fixed stop — but if the next journal shows
      // it cutting winners, this is the one line to flip.
      trailEnabled:      config.trailEnabled      ?? true,

      // ── Runner mode — the "let it ride" branch ──
      // Once peakR clears runnerActivateR the trade is a RUNNER: the
      // target-style exits (PEAK_GIVEBACK, BASE_DECAY, PILOT_TIME_CAP,
      // CONDITION_DEGRADED) stand down and the position is managed by the
      // ATR trail ALONE, at the wider runnerAtrMult. The ratchet never
      // loosens, so switching to the wider mult mid-trade can't drop the
      // stop — it only slows future tightening, which is exactly the room
      // a runner needs between legs. The flag is sticky for the trade.
      runnerActivateR:   config.runnerActivateR   ?? 2.5,
      // null = runners use the panel's trail mult. A number overrides it.
      runnerAtrMult:     config.runnerAtrMult     ?? null,
      // ATR period for the SELF-COMPUTED trail. The chart passes a
      // precomputed ctx.atrStop in local mode, but the server runner never
      // did — the journal's 93 trades show zero ATR_TRAIL exits because the
      // trail was dead everywhere it mattered. With no ctx.atrStop the bot
      // now computes its own Chandelier from ctx.candles.
      atrPeriod:         config.atrPeriod         ?? 5,

      // Risk rails
      maxDailyLossPct:   config.maxDailyLossPct   ?? 0.10,   // halt at -10% for the day
      maxTradesPerDay:   config.maxTradesPerDay   ?? 10,
      cooldownMs:        config.cooldownMs        ?? 60_000, // post-exit pause

      // Direction (V1 default: long-only)
      pilotDirection:    config.pilotDirection    ?? 'LONG',
    }
    this._normalizeConfig()

    // ─── Active base — the structure we're watching/piloting INTO ───
    this._activeBase = null

    // ─── Pilot (starter) position ───
    this._pilot = null

    // ─── Loaded (full) position ───
    this._load = null
    this._pos = null   // peak/trail state — the POSITION, not the leg
    this._lastMcap = 0

    // ─── Daily tracking ───
    this._dayStart = Date.now()
    this._dailyPnL = 0
    this._dailyTradeCount = 0
    this._cooldownUntil = 0

    // ─── Microstructure metrics ───
    this._tapeHeat = 0
    this._churnScore = 0
    this._lastDeltaZ = 0          // number; last entry-gate delta z (runner heartbeat reads it)
    this._vacuumScore = 0
    this._approachVelocity = 0
    this._densityRatio = 0
    this._reactivityNow = 0
    this._reactivityAvg = 0
    this._reactivityHistory = []
    this._lastReactivityClosedTime = 0
    this._lastHighlightMcap = 0

    // ─── DLMM bins (Meteora) ───
    this._meteoraBins = []
    this._meteoraBinsTotal = 0

    // ─── Raw swap buffer (reference to chart's buffer) ───
    this._rawSwaps = null

    // ─── Signal stream + analytics journal ───
    this.tradeLog = []
    this._journal = []
    this._journalCurrent = null
    this._nearMissLog = []
    this._loadJournal()
    this.stateLog = []
    this.lastSignal = null

    // ─── Callbacks ───
    this.onSignal = null
    this.onStateChange = null
  }

  // ============================================================
  // Public API
  // ============================================================

  enable() {
    this.enabled = true
    this._resetDailyIfNeeded()
    this._transition(STATES.SCANNING)
  }

  disable() {
    this.enabled = false
    this._activeBase = null
    this._pilot = null
    this._load = null
    this._pos = null
    this._transition(STATES.SCANNING)
  }

  toggle() {
    if (this.enabled) this.disable()
    else this.enable()
    return this.enabled
  }

  updateConfig(partial) {
    this.config = { ...this.config, ...partial }
    this._normalizeConfig()
  }

  // Map legacy / nonsensical values onto the current vocabulary so old saved
  // configs (local storage, droplet runner state) keep working.
  _normalizeConfig() {
    const c = this.config
    // Removed entry knobs (entry-gates2-20261018): drop them from old saved
    // configs / runner state so nothing reads a stale value.
    for (const k of ['entryMode', 'entryDz', 'basePosMax', 'maxAtrAbove', 'entryPaths']) delete c[k]
    // 'vwap' / 'off' stops never existed in the engine; with risk sizing a
    // stopless entry has no size. Both land on ATR.
    if (!['base', 'swing', 'atr', 'pct'].includes(c.pilotStopMode)) c.pilotStopMode = 'atr'
    if (c.pilotHoldMode !== 'off') c.pilotHoldMode = 'time'
    for (const k of ['ctxLiqBase', 'ctxValue', 'ctxStructure', 'ctxAbsorb', 'ctxTrend', 'ctxVwap', 'ctxFast', 'mktFilter', 'mktTheme']) c[k] = c[k] === true
    const num = (v, d, lo, hi) => { const n = Number(v); return Number.isFinite(n) ? Math.min(hi, Math.max(lo, n)) : d }
    c.ctxLiqBaseMin   = num(c.ctxLiqBaseMin, 30, 15, 240)
    c.ctxLiqBuyMin    = num(c.ctxLiqBuyMin, 0.42, 0, 1)
    c.ctxLiqMaxGapSec = num(c.ctxLiqMaxGapSec, 90, 5, 600)
    c.ctxValueTol     = num(c.ctxValueTol, 0.35, 0, 1)
    c.ctxAbsorbSec    = num(c.ctxAbsorbSec, 300, 15, 900)
    c.ctxAbsorbMult   = num(c.ctxAbsorbMult, 2, 1, 20)
    c.ctxTrendLH      = num(c.ctxTrendLH, 5, 1, 5)
    c.mktSol1hMin     = num(c.mktSol1hMin, -0.5, -50, 50)
    c.mktSol6hMin     = num(c.mktSol6hMin, -2.0, -50, 50)
    c.mktBreadthMin   = num(c.mktBreadthMin, 0.40, 0, 1)
    c.mktThemeMin     = num(c.mktThemeMin, 2, 1, 20)
    c.mktMaxAgeSec    = num(c.mktMaxAgeSec, 300, 60, 3600)
    if (!Array.isArray(c.lockSteps)) c.lockSteps = []
    c.lockSteps = c.lockSteps
      .filter(p => Array.isArray(p) && Number.isFinite(+p[0]) && Number.isFinite(+p[1]) && +p[1] < +p[0])
      .map(p => [+p[0], +p[1]])
      .sort((a, b) => a[0] - b[0])
  }

  /**
   * Main update — called per chart tick.
   * Microstructure metrics computed even when disabled (for HUD).
   * State machine only runs when enabled.
   */
  update(ctx) {
    const { candles, highlightMcap, meteoraBins, timeInterval } = ctx
    if (!candles || candles.length < 6 || !highlightMcap) return null
    // getStatus() takes no args and has no price of its own, but it now has to
    // report a LIVE peakR (derived, not stored). Stash the last price it can
    // derive against.
    this._lastMcap = highlightMcap
    this._solR(highlightMcap)   // reporting-only SOL-R high-water mark

    this._resetDailyIfNeeded()

    // ── Token change detection ─────────────────────────────────────
    // The bot's structural state (active base, reactivity history, any
    // open pilot/load) is token-specific. When the chart navigates to a
    // different token, none of it transfers, so we have to:
    //   1. Force-exit any managed position with reason TOKEN_SWITCH so
    //      the journal records the outcome honestly. We can't keep
    //      managing a trade whose tape we're no longer watching.
    //   2. Clear token-specific state (base, reactivity history).
    // Preserved across the switch: daily P/L, trade count, cooldown
    // timer, persistent journal, near-miss log — session state, not
    // token state.
    // (When the bot moves server-side and watches multiple tokens in
    // parallel, this whole block goes away — switching the chart
    // becomes a UI concern, not a bot concern.)
    const newToken = ctx?.token || null
    if (this._currentToken && newToken && newToken !== this._currentToken) {
      if (this.enabled && (this.state === STATES.PILOT || this.state === STATES.LOADED)) {
        // Use last known good price for the OLD token. If somehow we
        // never saw a tick for it, fall back to basis so pnlPct doesn't
        // compute as -100%.
        const exitMcap = this._lastHighlightMcap > 0
          ? this._lastHighlightMcap
          : (this._load?.blendedBasis || this._pilot?.mcap || 0)
        if (exitMcap > 0) {
          this._fireExit({ highlightMcap: exitMcap }, 'TOKEN_SWITCH')
        } else {
          // No usable price — drop the trade without journaling a fake
          // P/L. Better to lose the record than to record a lie.
          this._pilot = null
          this._load = null
          this._pos = null
          this._journalCurrent = null
          this._transition(STATES.SCANNING)
        }
      }
      this._resetTokenState()
    }

    this._lastHighlightMcap = highlightMcap
    // TAPE CLOCK: server-aligned now from the browser (ctx.now); null on the
    // droplet runner → Date.now(). Used ONLY where a time is compared against
    // trade timestamps — durations (cooldowns, caps) stay on the local clock,
    // where skew cancels.
    this._tapeNow = Number.isFinite(ctx?.now) ? ctx.now : null
    this._currentToken = newToken

    // Receive raw swap buffer ref
    if (ctx.rawSwaps !== undefined) this._rawSwaps = ctx.rawSwaps

    // DLMM bins
    if (Array.isArray(meteoraBins)) {
      const sorted = meteoraBins
        .filter(b => b && typeof b.mcap === 'number' && b.notionalSol > 0)
        .sort((a, b) => a.mcap - b.mcap)
      this._meteoraBins = sorted
      let total = 0
      for (const b of sorted) total += b.notionalSol || 0
      this._meteoraBinsTotal = total
    }

    // Always compute microstructure metrics (for HUD even when disabled)
    this._tapeHeat = this._computeTapeHeat(candles, timeInterval)
    this._churnScore = this._computeChurnScore(candles, timeInterval)
    this._computeReactivity(candles, timeInterval)

    // Always maintain the active base (structure read)
    this._updateBase(ctx)

    // Liquidity reaction speed — display only, never read by entries/exits.
    this._updateLiqReaction(ctx)

    // Entry context (entry-gates-20261018): session VWAP accumulates always
    // (cheap, incremental); the liquidity window only when a toggle needs it.
    try { this._updateSessionVwap(candles) } catch {}
    if (this.config.ctxLiqBase || this.config.ctxValue) {
      try { this._liqVa = this._computeLiqBase(candles) }
      catch { this._liqVa = { ok: false, full: false, why: 'LIQ_BASE_ERR' } }
    } else {
      this._liqVa = null
    }

    if (!this.enabled) return null

    // Stops on EVERY print since the last check, not just the latest one:
    // a print that went through the stop and bounced before this tick still
    // fires, at that print's price (bot-stops-20261017).
    const printExit = this._scanPrintStops(ctx)
    if (printExit) return printExit

    // State machine
    switch (this.state) {
      case STATES.SCANNING: return this._onScanning(ctx)
      case STATES.PILOT:    return this._onPilot(ctx)
      case STATES.LOADED:   return this._onLoaded(ctx)
      case STATES.COOLDOWN: return this._onCooldown(ctx)
      default: return null
    }
  }

  getStatus() {
    const now = Date.now()
    const cooldownRemaining = Math.max(0, this._cooldownUntil - now)
    const baseAbsorption = this._baseAbsorption()

    return {
      // Core state
      state:               this.state,
      enabled:             this.enabled,
      cooldownRemaining,

      // Daily
      dailyPnL:            this._dailyPnL,
      dailyTradeCount:     this._dailyTradeCount,

      // Active base (structure read)
      activeBase:          this._activeBase ? {
        durationMs:        this._activeBase.durationMs,
        durationSec:       Math.round(this._activeBase.durationMs / 1000),
        lowMcap:           this._activeBase.lowMcap,
        highMcap:          this._activeBase.highMcap,
        rangePct:          this._activeBase.startMcap > 0
                             ? (this._activeBase.highMcap - this._activeBase.lowMcap) / this._activeBase.startMcap
                             : 0,
        peakChurn:         this._activeBase.peakChurn,
        totalVol:          this._activeBase.totalVol,
        absorption:        baseAbsorption,
        qualified:         this._baseQualified(),
      } : null,
      // Foundations → Liquidity window read (null when Liquidity/Value off)
      liqBase:             this._liqVa ? this._liqSnap() : null,

      // Pilot
      pilot:               this._pilot ? {
        mcap:              this._pilot.mcap,
        sizeSol:           this._pilot.sizeSol,
        ageMs:             now - this._pilot.openedAt,
        stopMcap:          this._pilot.stopMcap,
        timeRemainingMs:   Math.max(0, this._pilot.timeCapAt - now),
      } : null,

      // Load
      load:                this._load ? {
        addMcap:           this._load.addMcap,
        addSizeSol:        this._load.addSizeSol,
        blendedBasis:      this._load.blendedBasis,
        direction:         this._load.direction,
        peakR:             this._solR(this._lastMcap || 0).peakR,
        peakR_mcap:        this._posR(this._lastMcap || 0).peakR,
        currentR:          this._solR(this._lastMcap || 0).currentR,
        atrStopMcap:       this._pos?.atrStopMcap || 0,
        stopMcap:          this._load.stopMcap || 0,
        totalSizeSol:      (this._pilot?.sizeSol || 0) + this._load.addSizeSol,
        adopted:           !!this._load.adopted,
        adoptedStopMcap:   this._load.adoptedStopMcap || 0,
        // Condition-degradation diagnostics. healthLast: current thesis
        // health (0..1). degradingForMs: how long health has been below
        // threshold (0 if currently healthy). Useful for HUDs that want
        // to show the bot getting nervous before it actually exits.
        healthLast:        this._load.healthLast ?? 0.5,
        degradingForMs:    this._load.degradedSince ? Math.max(0, now - this._load.degradedSince) : 0,
      } : null,

      // Last seen price (so UI can pre-fill adopt form with current price)
      lastMcap:            this._lastHighlightMcap,
      // Current charted token (mint for SOL, hl_<base> for HL futures)
      currentToken:        this._currentToken || null,

      // Microstructure (for HUD)
      tapeHeat:            this._tapeHeat,
      churnScore:          this._churnScore,
      vacuumScore:         this._vacuumScore,
      approachVelocity:    this._approachVelocity,
      densityRatio:        this._densityRatio,
      reactivityNow:       this._reactivityNow,
      reactivityAvg:       this._reactivityAvg,
      // Liquidity reaction speed (display only) — see _updateLiqReaction.
      liqReaction:         this._liqReactionStatus(),

      // DLMM
      dlmmBinCount:        this._meteoraBins.length,
      dlmmTotalSol:        this._meteoraBinsTotal,
      dlmmNextAskWall:     this._lastHighlightMcap ? this._nextWallAbove(this._lastHighlightMcap) : null,
      dlmmNextBidWall:     this._lastHighlightMcap ? this._nextWallBelow(this._lastHighlightMcap) : null,

      // Tape health
      rawSwapCount:        this._rawSwaps?.length || 0,
      rawSwap30sCount:     this._rawSwaps?.length ? this._swapsInWindow(30).length  : 0,
      rawSwap5mCount:      this._rawSwaps?.length ? this._swapsInWindow(300).length : 0,

      // Entry-funnel telemetry — the last few near-misses (newest first),
      // riding the status broadcast so the UI/runner can show WHY the bot
      // declined without any extra plumbing. Bounded; each entry carries
      // missReason + the failing numbers vs their minimums.
      nearMisses:          this._nearMissLog.slice(-8).reverse(),
      loadGate:            this._loadGateLast || null,   // last gate read (why loads are/aren't allowed)
      entryGate:           this._entryGateLast || null,  // last entry-location gate read
      position:            this._pos ? {
        lockStopMcap:      this._pos.lockStopMcap || 0,
        lockR:             this._pos.lockR ?? null,
        trailArmed:        !!this._pos.trailArmed,
        atrStopMcap:       this._pos.atrStopMcap || 0,
        remainFrac:        this._pos.remainFrac ?? 1,
        realizedSol:       this._pos.realizedSol || 0,
        runner:            !!this._pos.runnerMode,
        effStopMcap:       this._effStop() || 0,
      } : null,

      // ─── Legacy fields (read by older UI; kept null/0 so reads don't crash) ───
      setupType:           null,
      tradeDirection:      this._load?.direction || null,
      holdStage:           null,
      accumulationCount:   0,
      targetZone:          null,
      nearQualifiedZone:   false,
      entryMcap:           this._pilot?.mcap || 0,
      stopMcap:            this._effStop() || this._pilot?.stopMcap || 0,
      peakR:               this._solR(this._lastMcap || 0).peakR,
      peakR_mcap:          this._posR(this._lastMcap || 0).peakR,
      peakMcap:            this._pos?.peakMcap || 0,
    }
  }

  // ============================================================
  // Persistence
  // ============================================================

  serialize() {
    return {
      v: 2,
      enabled: this.enabled,
      config: { ...this.config },
      dailyPnL: this._dailyPnL,
      dailyTradeCount: this._dailyTradeCount,
      dayStart: this._dayStart,
    }
  }

  static deserialize(json, overrides = {}) {
    if (!json || (typeof json !== 'object' && typeof json !== 'string')) return new OrderflowBot(overrides)
    try {
      const data = typeof json === 'string' ? JSON.parse(json) : json
      // Only carry forward config from v2 schema — v1 configs had irrelevant
      // keys that would pollute the new bot.
      const carry = data.v === 2 ? migrateBotConfig({ ...(data.config || {}) }) : {}
      const bot = new OrderflowBot({ ...carry, ...overrides })
      if (data.v === 2) {
        bot._dailyPnL = data.dailyPnL || 0
        bot._dailyTradeCount = data.dailyTradeCount || 0
        bot._dayStart = data.dayStart || Date.now()
      }
      return bot
    } catch {
      return new OrderflowBot(overrides)
    }
  }

  // ============================================================
  // Journal (persistent trade analytics)
  // ============================================================

  static JOURNAL_KEY = 'solcheddar_bot_journal'
  static JOURNAL_MAX_ENTRIES = 500

  _loadJournal() {
    try {
      const raw = localStorage.getItem(OrderflowBot.JOURNAL_KEY)
      if (!raw) return
      const parsed = JSON.parse(raw)
      if (Array.isArray(parsed)) this._journal = parsed
    } catch {}
  }

  _saveJournal() {
    try {
      if (this._journal.length > OrderflowBot.JOURNAL_MAX_ENTRIES) {
        this._journal = this._journal.slice(-OrderflowBot.JOURNAL_MAX_ENTRIES)
      }
      localStorage.setItem(OrderflowBot.JOURNAL_KEY, JSON.stringify(this._journal))
    } catch (e) {
      console.warn('[bot.journal] save failed:', e?.message || e)
    }
  }

  getJournal() { return [...this._journal].reverse() }

  resetJournal() {
    const count = this._journal.length
    this._journal = []
    this._journalCurrent = null
    try { localStorage.removeItem(OrderflowBot.JOURNAL_KEY) } catch {}
    return count
  }

  /** Near-miss log — setups that ALMOST qualified. Filter calibration data. */
  getNearMisses() { return [...this._nearMissLog].reverse() }

  _snapshotMetrics(ctx) {
    return {
      h: this._tapeHeat || 0,
      c: this._churnScore || 0,
      r: this._reactivityNow || 0,
      rAvg: this._reactivityAvg || 0,
      askWall: this._meteoraBins?.length ? this._nextWallAbove(ctx?.highlightMcap || 0) : null,
      bidWall: this._meteoraBins?.length ? this._nextWallBelow(ctx?.highlightMcap || 0) : null,
    }
  }

  // ============================================================
  // Base tracker — maintains _activeBase as churn comes and goes
  // ============================================================

  _updateBase(ctx) {
    const { highlightMcap } = ctx
    const churn = this._churnScore
    const now = Date.now()

    if (!this._activeBase) {
      // Open a new base if churn just activated meaningfully
      if (churn >= BASE_OPEN_CHURN) {
        // Snapshot the AMBIENT baseline rate NOW, from the 300s BEFORE open.
        // The old absorption math measured baseline from the trailing 300s at
        // qualification time — a window the base itself dominates — which
        // capped absorption at ~300/durationSec (1.67x at the 180s minimum,
        // →1.0x at 300s). The 2.0x threshold was unreachable except for
        // bases whose volume had collapsed: the filter selected DYING bases,
        // the opposite of the thesis. Pre-open ambient is the honest divisor.
        let blVol = 0
        let blCoverageSec = 0
        if (this._rawSwaps && this._rawSwaps.length) {
          const openMs = this._nowTape()
          const winStart = openMs - 300_000
          let oldestSeen = openMs
          for (let i = this._rawSwaps.length - 1; i >= 0; i--) {
            const t = this._rawSwaps[i].time || 0
            if (t >= openMs) continue
            if (t < winStart) { oldestSeen = winStart; break }
            blVol += (this._rawSwaps[i].amount || 0)
            if (t < oldestSeen) oldestSeen = t
          }
          blCoverageSec = (openMs - oldestSeen) / 1000
        }
        // <30s of pre-open tape = ambient rate unknown; store 0 and let
        // _baseAbsorption return 0 (base can't qualify on a made-up number).
        const baselineRate = blCoverageSec >= 30 ? blVol / blCoverageSec : 0
        this._activeBase = {
          startTs: now,
          lastTs: now,
          durationMs: 0,
          startMcap: highlightMcap,
          lowMcap: highlightMcap,
          highMcap: highlightMcap,
          peakChurn: churn,
          totalVol: 0,
          baselineRate,
          lastVolTs: this._rawSwaps?.length ? this._rawSwaps[this._rawSwaps.length - 1].time : now,
          deadSince: 0,
        }
      }
      return
    }

    // Update existing base
    const b = this._activeBase
    b.lastTs = now
    b.durationMs = now - b.startTs
    if (highlightMcap < b.lowMcap) b.lowMcap = highlightMcap
    if (highlightMcap > b.highMcap) b.highMcap = highlightMcap
    if (churn > b.peakChurn) b.peakChurn = churn

    // Accumulate volume incrementally — count only swaps newer than lastVolTs
    if (this._rawSwaps && this._rawSwaps.length) {
      for (let i = this._rawSwaps.length - 1; i >= 0; i--) {
        const sw = this._rawSwaps[i]
        if ((sw.time || 0) <= b.lastVolTs) break
        b.totalVol += (sw.amount || 0)
      }
      b.lastVolTs = this._rawSwaps[this._rawSwaps.length - 1].time
    }

    // Decay tracking — base dies if churn stays below threshold for grace period
    if (churn < BASE_DEAD_CHURN) {
      if (!b.deadSince) b.deadSince = now
      if (now - b.deadSince > BASE_CLOSE_GRACE_MS) {
        // Base decayed without resolution. If we're in PILOT, _onPilot will
        // see _activeBase=null and exit. If we're scanning, just clear.
        //
        // NEAR-MISS TELEMETRY: base qualification failing was the one SILENT
        // gate in the entry funnel (near-misses only logged from BOXED_IN
        // onward) — "finds great tokens, never trades, no trace" was this.
        // Log ONE entry per dying unqualified base: its final numbers vs the
        // three minimums and exactly which failed. Pure instrumentation — no
        // threshold or strategy change; the data decides if 3min/5.0/2.0 are
        // miscalibrated for discovery-grade movers, not a hunch.
        if (!this._baseQualified()) {
          b.durationMs = now - b.startTs
          const absorption = this._baseAbsorption()
          const failed = []
          if (b.durationMs < BASE_MIN_DURATION_MS) failed.push('DURATION')
          if (b.peakChurn < BASE_MIN_PEAK_CHURN) failed.push('CHURN')
          if (this.config.ctxLiqBase) { if (!this._liqVa?.ok) failed.push('LIQ_BASE') }
          else if (absorption < BASE_MIN_ABSORPTION) failed.push('ABSORPTION')
          this._logNearMiss(ctx, 'BASE_DISQUALIFIED', {
            failed: failed.join('+') || 'NONE',
            baseDurationMs: b.durationMs,
            needDurationMs: BASE_MIN_DURATION_MS,
            basePeakChurn: +b.peakChurn.toFixed(2),
            needPeakChurn: BASE_MIN_PEAK_CHURN,
            baseAbsorption: +absorption.toFixed(2),
            needAbsorption: BASE_MIN_ABSORPTION,
          })
        }
        this._activeBase = null
      }
    } else {
      b.deadSince = 0
    }
  }

  /** Total absorbed vol / (pre-open ambient rate × base duration in sec).
   *  >1.0 means the base absorbed more than ambient activity = trapped inventory.
   *  Baseline is snapshotted at base OPEN from the 300s before it (see
   *  _updateBase) — a trailing window here would include the base itself. */
  _baseAbsorption() {
    const b = this._activeBase
    if (!b || b.durationMs < 1000) return 0
    const blRate = b.baselineRate || 0
    if (blRate <= 0) return 0
    const expected = blRate * (b.durationMs / 1000)
    return expected > 0 ? b.totalVol / expected : 0
  }

  /** Does the active base meet all qualification criteria? */
  _baseQualified() {
    const b = this._activeBase
    if (!b) return false
    if (b.durationMs < BASE_MIN_DURATION_MS) return false
    if (b.peakChurn < BASE_MIN_PEAK_CHURN) return false
    // Foundations → Liquidity replaces the absorption ≥ 2.0 rule.
    if (this.config.ctxLiqBase) { if (!this._liqVa?.ok) return false }
    else if (this._baseAbsorption() < BASE_MIN_ABSORPTION) return false
    return true
  }

  // ============================================================
  // State handlers
  // ============================================================

  _onScanning(ctx) {
    const { highlightMcap } = ctx
    if (Date.now() < this._cooldownUntil) return null
    if (this._dailyTradeCount >= this.config.maxTradesPerDay) return null
    if (this._dailyPnL <= -this.config.maxDailyLossPct) return null
    if (!this._baseQualified()) {
      // Liquidity on: a churn base that is otherwise mature but has no
      // liquidity base behind it logs WHY (LIQ_BASE_SHORT / _SELLERS / _GAP).
      const b = this._activeBase
      if (this.config.ctxLiqBase && b && b.durationMs >= BASE_MIN_DURATION_MS && b.peakChurn >= BASE_MIN_PEAK_CHURN) {
        const g = { pass: false, why: this._liqVa?.why || 'LIQ_BASE_NONE', ctx: { liq: this._liqSnap() } }
        this._entryGateLast = { ...g, at: Date.now() }
        this._recordSkip(ctx, g)
      }
      return null
    }

    // Entry location gate — no buying the top of a base / an extended run.
    // Applies to every entry, re-entries after a stop included.
    let gate = this._entryGate(ctx)
    // Foundations: Structure → Absorption cells → Trend (all off by default).
    if (gate.pass) {
      const cg = this._contextGate(ctx)
      if (cg.ctx && Object.keys(cg.ctx).length) gate = { ...gate, ctx: { ...(gate.ctx || {}), ...cg.ctx } }
      if (!cg.pass) gate = { ...gate, pass: false, why: cg.why }
    }
    // Market: broad-market health + hot theme (both off by default).
    if (gate.pass) {
      const mg = this._marketGate(ctx)
      if (mg.ctx) gate = { ...gate, ctx: { ...(gate.ctx || {}), market: mg.ctx } }
      if (!mg.pass) gate = { ...gate, pass: false, why: mg.why }
    }
    this._entryGateLast = { ...gate, at: Date.now() }
    if (!gate.pass) {
      this._recordSkip(ctx, gate)
      return null
    }

    // Liquidity gap check — boxed in by walls on both sides = no viable gap
    const wallAbove = this._nextWallAbove(highlightMcap)
    const wallBelow = this._nextWallBelow(highlightMcap)
    const boxedIn = wallAbove && wallBelow
      && wallAbove.distancePct < WALL_BOX_BOTH_SIDES_PCT
      && wallBelow.distancePct < WALL_BOX_BOTH_SIDES_PCT
    if (boxedIn) {
      this._logNearMiss(ctx, 'BOXED_IN', { wallAbove, wallBelow })
      return null
    }

    return this._firePilot(ctx)
  }

  _onPilot(ctx) {
    const { highlightMcap } = ctx
    const now = Date.now()
    if (!this._pilot) {
      this._transition(STATES.SCANNING)
      return null
    }

    // Runner mode: time-cap and base-decay stand down — a pilot that ran to
    // runnerActivateR is no longer "waiting for the imbalance", it IS the
    // trade, and only the ATR trail (via _managePosition) may end it.
    const _isRunner = !!(this._pos && this._pos.runnerMode)

    // 1. Time cap — pilot held too long without imbalance
    if (!_isRunner && this._pilot.timeCapAt > 0 && now >= this._pilot.timeCapAt) {
      return this._fireExit(ctx, 'PILOT_TIME_CAP')
    }

    // 2. Base decayed without resolution
    if (!_isRunner && !this._activeBase) {
      return this._fireExit(ctx, 'BASE_DECAY')
    }

    // 3a. Profit-lock ladder stop (BE_LOCK / LOCK_STEP) — sits above the
    //     initial stop once armed, so it is checked first.
    const lockHit = this._lockBreach(highlightMcap)
    if (lockHit) return this._fireExit(ctx, lockHit)

    // 3. Initial stop (long-only V1: stop is below entry)
    const stopBreached = this.config.pilotDirection === 'LONG'
      ? highlightMcap <= this._pilot.stopMcap
      : highlightMcap >= this._pilot.stopMcap
    if (stopBreached) {
      return this._fireExit(ctx, 'PILOT_STOP')
    }

    // 4. POSITION MANAGEMENT — peak, trail, giveback.
    //    This line is the whole fix. It did not exist. A pilot that never loaded
    //    had no high-water mark, no trail and no profit-aware exit of any kind:
    //    it could only leave by fixed stop, base decay, or time cap. All three
    //    are blind to profit, which is why the winners came home flat — BASE_DECAY
    //    median +0.05R is not the market refusing to pay, it's a timeout leaving
    //    at whatever price happened to be there.
    const managed = this._managePosition(ctx)
    if (managed) return this._fireExit(ctx, managed)

    // 5. Imbalance fired? — check for vol expansion with directional bias
    // No adds after a scale-out — the size math assumes one entry shape.
    const trigger = (this._pos && this._pos.scaled) ? null : this._checkLoadTrigger(ctx)
    if (trigger) {
      // Load liquidity/volatility gate. A rising-imbalance trigger on a thin
      // or flat book is exactly the journal's -8R…-31R cohort — adding size
      // there is the drawdown engine. Block the LOAD only; the pilot keeps
      // running with its own stop/trail/decay exits.
      const gate = this._loadValueGate(highlightMcap)
      this._loadGateLast = { ...gate, at: now }
      if (!gate.pass) {
        // Throttled diagnostic — the trigger can stay true for many ticks.
        if (!this._lastGateBlockAt || now - this._lastGateBlockAt > 30_000) {
          this._lastGateBlockAt = now
          if (this._nearMissLog.length >= 100) this._nearMissLog.shift()
          this._nearMissLog.push({
            type: 'LOAD_GATE_BLOCK', time: now, mcap: highlightMcap,
            why: gate.why, rangePct: +(gate.rangePct || 0).toFixed(2), valueRank: gate.valueRank || 0,
          })
        }
        return null
      }
      return this._fireLoad(ctx, { ...trigger, gate })
    }

    return null
  }

  // ============================================================
  // Position management — SHARED by PILOT and LOADED.
  // ============================================================
  // Basis / stop / risk for whichever leg is live. The load blends the basis
  // and may carry its own stop; the pilot has neither. One reader, so the R-math
  // can't disagree with itself depending on which state we happen to be in.
  _posRef() {
    if (this._load) {
      const basis = this._load.blendedBasis
      const isLong = this._load.direction === 'LONG'
      const fallback = isLong
        ? basis * (1 - this.config.pilotStopPct)
        : basis * (1 + this.config.pilotStopPct)
      // Priority: adopted stop (user-specified) > the LOAD's own stop (set
      // at _fireLoad from the trigger's structural floor — fix for
      // INITIAL_STOP booking -8R: 6/7 of the size used to be protected only
      // by the pilot's stop far below the load entry) > pilot stop > pct
      // fallback.
      const stop = this._load.adoptedStopMcap || this._load.stopMcap || this._pilot?.stopMcap || fallback
      return { basis, stop, isLong, risk: Math.abs(basis - stop), adopted: !!this._load.adopted }
    }
    if (this._pilot) {
      const basis = this._pilot.mcap
      const isLong = this.config.pilotDirection === 'LONG'
      const stop = this._pilot.stopMcap
      return { basis, stop, isLong, risk: Math.abs(basis - stop), adopted: false }
    }
    return null
  }

  // Live R against the current basis. Nothing is ratcheted except peakMcap.
  _posR(highlightMcap) {
    const p = this._posRef()
    if (!p || !this._pos || !(p.risk > 0)) return { peakR: 0, currentR: 0 }
    const sign = p.isLong ? 1 : -1
    return {
      peakR:    (this._pos.peakMcap - p.basis) * sign / p.risk,
      currentR: (highlightMcap      - p.basis) * sign / p.risk,
    }
  }

  // REPORTING-ONLY R: SOL P&L (booked + open legs) over the original SOL risk
  // budget. Can't inflate after a load the way _posR's basis→stop unit does.
  // Exits never read this. Ratchets pos.peakRSol (a field no exit reads).
  _solR(highlightMcap) {
    const pos = this._pos
    if (!pos || !(pos.riskSolBudget > 0) || !(highlightMcap > 0)) return { peakR: 0, currentR: 0 }
    const isLong = (this._load?.direction || this.config.pilotDirection) === 'LONG'
    const pnl = (pos.realizedSol || 0) + this._openLegsPnlSol(highlightMcap, isLong ? 1 : -1)
    const currentR = pnl / pos.riskSolBudget
    if (!(currentR <= (pos.peakRSol ?? -Infinity))) pos.peakRSol = currentR
    return { peakR: pos.peakRSol, currentR }
  }

  // ============================================================
  // Bar helpers — mgmt bars (bot bars merged up to mgmtBarSec)
  // ============================================================
  _hi(c) { return c.high_mcap ?? c.high ?? 0 }
  _lo(c) { return c.low_mcap ?? c.low ?? 0 }
  _cl(c) { return c.close_mcap ?? c.close ?? 0 }
  _op(c) { return c.open_mcap ?? c.open ?? 0 }

  /** Completed bars merged into mgmtBarSec buckets. Bot bars already at or
   *  above that size pass through one-for-one. The forming bucket is dropped. */
  _mgmtBars(candles) {
    if (!Array.isArray(candles) || candles.length < 2) return []
    const sec = Math.max(1, this.config.mgmtBarSec || 60) * 1000
    const done = candles.slice(0, -1)
    const lastLiveT = candles[candles.length - 1]?.startTime
    const out = []
    let cur = null
    for (const c of done) {
      const h = this._hi(c), l = this._lo(c), cl = this._cl(c), o = this._op(c) || cl
      if (!(h > 0) || !(l > 0) || !(cl > 0)) continue
      const t = Number.isFinite(c.startTime) ? Math.floor(c.startTime / sec) : null
      if (cur && t !== null && cur.t === t) {
        if (h > cur.h) cur.h = h
        if (l < cur.l) cur.l = l
        cur.c = cl
        cur.flow += this._barDelta(c)
        cur.vol += this._barVol(c)
      } else {
        if (cur) out.push(cur)
        cur = { t, o, h, l, c: cl, flow: this._barDelta(c), vol: this._barVol(c) }
      }
    }
    // The bucket the live bar belongs to is still forming — drop it.
    if (cur) {
      const liveBucket = Number.isFinite(lastLiveT) ? Math.floor(lastLiveT / sec) : null
      if (!(liveBucket !== null && cur.t === liveBucket)) out.push(cur)
    }
    return out
  }

  // Buy/sell flow of one bot bar: mcapLevels, or envelope cells [[mcap,buy,sell]].
  _barFlowPair(c) {
    let buy = 0, sell = 0
    const lv = c && c.mcapLevels
    if (lv && Object.keys(lv).length) {
      for (const k in lv) { buy += lv[k]?.buy || 0; sell += lv[k]?.sell || 0 }
    } else if (Array.isArray(c?.cells)) {
      for (const x of c.cells) { buy += +x?.[1] || 0; sell += +x?.[2] || 0 }
    }
    return [buy, sell]
  }
  _barDelta(c) { const [b, s] = this._barFlowPair(c); return b - s }
  _barVol(c)   { const [b, s] = this._barFlowPair(c); return b + s }

  /** Wilder ATR series over bars ({h,l,c}). Returns last value or 0. */
  _atrLast(bars, n) {
    n = Math.max(1, n | 0)
    if (!bars || bars.length < n + 1) return 0
    const tr = []
    for (let i = 1; i < bars.length; i++) {
      const b = bars[i], pc = bars[i - 1].c
      tr.push(Math.max(b.h - b.l, Math.abs(b.h - pc), Math.abs(b.l - pc)))
    }
    if (tr.length < n) return 0
    let atr = 0
    for (let i = 0; i < n; i++) atr += tr[i]
    atr /= n
    for (let i = n; i < tr.length; i++) atr = (atr * (n - 1) + tr[i]) / n
    return atr
  }

  /** ATR trailing stop — same formula as swingUtils.computeATRTrailingStop:
   *  Wilder ATR(n), stopLine = high − mult×ATR, TS = highest stopLine over
   *  the last hhv bars. HHV is honoured (it used to be ignored). Fresh coins
   *  with fewer bars use a partial HHV window. Returns 0 if not computable. */
  _atrTrailLast(bars, n, hhv, mult) {
    n = Math.max(1, n | 0); hhv = Math.max(1, hhv | 0)
    if (!bars || bars.length < n + 2 || !(mult > 0)) return 0
    const tr = [0]
    for (let i = 1; i < bars.length; i++) {
      const b = bars[i], pc = bars[i - 1].c
      tr.push(Math.max(b.h - b.l, Math.abs(b.h - pc), Math.abs(b.l - pc)))
    }
    const atr = new Array(bars.length).fill(0)
    let sum = 0
    for (let i = 1; i <= n; i++) sum += tr[i]
    atr[n] = sum / n
    for (let i = n + 1; i < bars.length; i++) atr[i] = (atr[i - 1] * (n - 1) + tr[i]) / n
    let ts = -Infinity
    for (let i = Math.max(n, bars.length - hhv); i < bars.length; i++) {
      const line = bars[i].h - mult * atr[i]
      if (line > ts) ts = line
    }
    return ts > 0 ? ts : 0
  }

  /** Most recent pivot low (k bars each side) strictly below `below`. */
  _swingLowBelow(bars, below, k) {
    k = Math.max(1, k | 0)
    for (let i = bars.length - 1 - k; i >= k; i--) {
      const l = bars[i].l
      if (!(l < below)) continue
      let pivot = true
      for (let j = 1; j <= k && pivot; j++) {
        if (!(l <= bars[i - j].l) || !(l <= bars[i + j].l)) pivot = false
      }
      if (pivot) return l
    }
    return 0
  }

  /** Zero-centred delta z of the last completed bot bar vs the RMS of the
   *  previous ≤20 bars. null when there's not enough flow to say. */
  // NOTE: bot_runner's heartbeat reads `_lastDeltaZ` as a NUMBER field —
  // this method must never be named that (a method there crash-looped the
  // droplet on .toFixed). The latest value is stored in this._lastDeltaZ.
  _computeLastDeltaZ(candles) {
    if (!Array.isArray(candles) || candles.length < 10) return null
    const done = candles.slice(0, -1).filter(c => c && !c.isHistorical)
    if (done.length < 9) return null
    const last = this._barDelta(done[done.length - 1])
    const prev = done.slice(-21, -1).map(c => this._barDelta(c))
    const rms = Math.sqrt(prev.reduce((a, d) => a + d * d, 0) / prev.length)
    if (!(rms > 0)) return null
    return last / rms
  }

  // ============================================================
  // Entry location gate
  // ============================================================
  // Location + guards. Foundations → Value (value-area low of the liquidity
  // window) and → VWAP (extension vs session VWAP), plus Max up bars. With
  // Value off there is no location check. dz/basePos stay as telemetry.
  _entryGate(ctx) {
    const cfg = this.config
    const price = ctx.highlightMcap
    const b = this._activeBase
    const out = { pass: true, why: 'ok' }
    if (!b) return out

    const bars = this._mgmtBars(ctx.candles)
    const atr = this._atrLast(bars, cfg.atrPeriod || 5)
    const vwap = this._sessionVwapAll()
    const range = b.highMcap - b.lowMcap
    const basePos = range > 0 ? (price - b.lowMcap) / range : 0
    const dz = this._computeLastDeltaZ(ctx.candles)
    this._lastDeltaZ = Number.isFinite(dz) ? dz : 0
    let upBars = 0
    for (let i = bars.length - 1; i >= 1; i--) {
      if (bars[i].c > bars[i - 1].c) upBars++
      else break
    }
    const extAtr = atr > 0 && vwap > 0 ? (price - vwap) / atr : null
    Object.assign(out, {
      basePos: +basePos.toFixed(3), atr: +atr.toFixed(4), vwap: +vwap.toFixed(4),
      extAtr: extAtr == null ? null : +extAtr.toFixed(2), dz: dz == null ? null : +dz.toFixed(2), upBars,
      ctx: {},
    })
    if (cfg.ctxLiqBase || cfg.ctxValue) out.ctx.liq = this._liqSnap()

    if (cfg.maxUpBars != null && upBars > cfg.maxUpBars) {
      return { ...out, pass: false, why: 'EXTENDED_RUN' }
    }
    if (cfg.ctxVwap && cfg.maxExtAtr != null && extAtr != null && extAtr > cfg.maxExtAtr) {
      return { ...out, pass: false, why: 'EXTENDED_ATR' }
    }
    if (cfg.ctxValue) {
      const lb = this._liqVa
      if (!(lb && lb.full && lb.val > 0 && lb.vah >= lb.val)) return { ...out, pass: false, why: 'VALUE_NA' }
      const lim = lb.val + (cfg.ctxValueTol ?? 0.35) * (lb.vah - lb.val)
      out.ctx.valueMax = +lim.toFixed(4)
      if (price > lim) return { ...out, pass: false, why: 'NOT_LOW_IN_VALUE' }
      return { ...out, why: 'VALUE_LOW' }
    }
    return { ...out, why: 'NO_LOCATION_CHECK' }
  }

  // ============================================================
  // Entry context — structural foundations (entry-gates-20261018)
  // ------------------------------------------------------------
  // Every read here comes from ctx.candles (bot bars: 15s on the droplet,
  // the chart's bars in the browser). rawSwaps is NOT used — the droplet
  // keeps only ~120s of prints. Bars with no buy/sell split (plain
  // historical) are skipped: no split = no two-sided read.
  // ============================================================

  /** Bar length in ms from candle spacing (smallest recent step). */
  _barMs(candles) {
    let best = Infinity
    if (Array.isArray(candles)) {
      for (let i = Math.max(1, candles.length - 30); i < candles.length; i++) {
        const d = (candles[i]?.startTime || 0) - (candles[i - 1]?.startTime || 0)
        if (d > 0 && d < best) best = d
      }
    }
    return Number.isFinite(best) ? best : 60_000
  }

  /** [[mcap, buy, sell], ...] of one bar, from mcapLevels or cells. */
  _barLevels(c) {
    const out = []
    const lv = c && c.mcapLevels
    if (lv && Object.keys(lv).length) {
      for (const k in lv) {
        const m = Number(k), b = lv[k]?.buy || 0, s = lv[k]?.sell || 0
        if (Number.isFinite(m) && m > 0 && b + s > 0) out.push([m, b, s])
      }
    } else if (Array.isArray(c?.cells)) {
      for (const x of c.cells) {
        const m = +x?.[0], b = +x?.[1] || 0, s = +x?.[2] || 0
        if (Number.isFinite(m) && m > 0 && b + s > 0) out.push([m, b, s])
      }
    }
    return out
  }

  /** Bars with startTime ≥ fromMs (forming bar included), ascending, as
   *  { t, h, l, c, buy, sell, lv }. */
  _ctxBars(candles, fromMs) {
    const out = []
    if (!Array.isArray(candles)) return out
    for (let i = candles.length - 1; i >= 0; i--) {
      const c = candles[i]
      const t = c?.startTime
      if (!Number.isFinite(t)) continue
      if (t < fromMs) break
      const h = this._hi(c), l = this._lo(c), cl = this._cl(c)
      if (!(h > 0) || !(l > 0) || !(cl > 0)) continue
      const lv = this._barLevels(c)
      let buy = 0, sell = 0
      for (const x of lv) { buy += x[1]; sell += x[2] }
      if (!(buy + sell > 0)) continue
      out.push({ t, h, l, c: cl, buy, sell, lv })
    }
    return out.reverse()
  }

  /** Merge ctx bars into `ms` buckets (levels summed per mcap). */
  _mergeBars(bars, ms, dropForming) {
    const out = []
    let cur = null
    const addLv = (o, lv) => {
      for (const [m, b, s] of lv) {
        const x = o._m.get(m)
        if (x) { x[0] += b; x[1] += s } else o._m.set(m, [b, s])
      }
    }
    for (const b of bars) {
      const k = Math.floor(b.t / ms) * ms
      if (cur && cur.t === k) {
        if (b.h > cur.h) cur.h = b.h
        if (b.l < cur.l) cur.l = b.l
        cur.c = b.c; cur.buy += b.buy; cur.sell += b.sell
        addLv(cur, b.lv)
      } else {
        if (cur) out.push(cur)
        cur = { t: k, h: b.h, l: b.l, c: b.c, buy: b.buy, sell: b.sell, _m: new Map() }
        addLv(cur, b.lv)
      }
    }
    if (cur) out.push(cur)
    for (const o of out) { o.lv = [...o._m].map(([m, [b, s]]) => [m, b, s]); delete o._m }
    if (dropForming && out.length) {
      const nowK = Math.floor(this._nowTape() / ms) * ms
      if (out[out.length - 1].t >= nowK) out.pop()
    }
    return out
  }

  /** 70% value area of the bars' volume-at-price → { val, vah, poc, vol }. */
  _valueArea(bars) {
    const prof = new Map()
    let tot = 0
    for (const b of bars) for (const [m, bu, se] of b.lv) {
      const v = bu + se
      prof.set(m, (prof.get(m) || 0) + v)
      tot += v
    }
    if (!(tot > 0) || !prof.size) return null
    const keys = [...prof.keys()].sort((a, b) => a - b)
    let pi = 0
    for (let i = 1; i < keys.length; i++) if (prof.get(keys[i]) > prof.get(keys[pi])) pi = i
    let lo = pi, hi = pi, acc = prof.get(keys[pi])
    while (acc < 0.7 * tot && (lo > 0 || hi < keys.length - 1)) {
      const dn = lo > 0 ? prof.get(keys[lo - 1]) : -1
      const up = hi < keys.length - 1 ? prof.get(keys[hi + 1]) : -1
      if (up >= dn) { hi++; acc += up } else { lo--; acc += dn }
    }
    return { val: keys[lo], vah: keys[hi], poc: keys[pi], vol: tot }
  }

  /** Foundations → Liquidity: rolling ctxLiqBaseMin window read.
   *  ok = full window + buy share ≥ ctxLiqBuyMin + no gap > ctxLiqMaxGapSec.
   *  full = the bars cover ≥ 90% of the window (Value needs this too). */
  _computeLiqBase(candles) {
    const cfg = this.config
    const winMs = (cfg.ctxLiqBaseMin || 30) * 60_000
    const now = this._nowTape()
    const barMs = this._barMs(candles)
    const bars = this._ctxBars(candles, now - winMs)
    const out = { ok: false, full: false, why: 'LIQ_BASE_SHORT', winMin: winMs / 60_000, coverMin: 0 }
    if (bars.length < 2) return out
    const coverMs = now - bars[0].t
    out.coverMin = +(coverMs / 60_000).toFixed(1)
    let buy = 0, sell = 0, maxGap = 0
    for (let i = 0; i < bars.length; i++) {
      buy += bars[i].buy; sell += bars[i].sell
      if (i > 0) maxGap = Math.max(maxGap, bars[i].t - (bars[i - 1].t + barMs))
    }
    maxGap = Math.max(maxGap, now - (bars[bars.length - 1].t + barMs))
    out.buyShare = buy + sell > 0 ? +(buy / (buy + sell)).toFixed(3) : 0
    out.maxGapSec = Math.round(Math.max(0, maxGap) / 1000)
    out.vol = +(buy + sell).toFixed(2)
    const va = this._valueArea(bars)
    if (va) { out.val = va.val; out.vah = va.vah; out.poc = va.poc }
    out.full = coverMs >= 0.9 * winMs && !!va
    if (!out.full) return out                                   // LIQ_BASE_SHORT
    if (out.buyShare < cfg.ctxLiqBuyMin) return { ...out, why: 'LIQ_BASE_SELLERS' }
    if (out.maxGapSec > cfg.ctxLiqMaxGapSec) return { ...out, why: 'LIQ_BASE_GAP' }
    return { ...out, ok: true, why: 'ok' }
  }

  _liqSnap() {
    const L = this._liqVa
    if (!L) return null
    const r = (v) => (Number.isFinite(v) ? +(+v).toFixed(4) : null)
    return { ok: !!L.ok, full: !!L.full, why: L.why, val: r(L.val), vah: r(L.vah), poc: r(L.poc),
      buyShare: L.buyShare ?? null, maxGapSec: L.maxGapSec ?? null, coverMin: L.coverMin, winMin: L.winMin }
  }

  /** Foundations → VWAP: running VWAP over every bar seen for this coin
   *  (keys survive the candle store trimming old bars). Reset on token switch. */
  _updateSessionVwap(candles) {
    if (!Array.isArray(candles) || !candles.length) return
    const S = this._sv || (this._sv = { map: new Map(), pv: 0, v: 0, doneT: -Infinity })
    for (let i = candles.length - 1; i >= 0; i--) {
      const c = candles[i]
      const t = c?.startTime
      if (!Number.isFinite(t)) continue
      if (t < S.doneT) break
      const h = this._hi(c), l = this._lo(c), cl = this._cl(c)
      const vol = this._barVol(c)
      const ok = h > 0 && l > 0 && cl > 0 && vol > 0
      const pv = ok ? ((h + l + cl) / 3) * vol : 0
      const v = ok ? vol : 0
      const old = S.map.get(t)
      if (old) { S.pv -= old[0]; S.v -= old[1] }
      S.map.set(t, [pv, v])
      S.pv += pv; S.v += v
    }
    const n = candles.length
    if (n >= 2 && Number.isFinite(candles[n - 2]?.startTime)) S.doneT = candles[n - 2].startTime
  }
  _sessionVwapAll() {
    const S = this._sv
    return S && S.v > 0 && S.pv > 0 ? S.pv / S.v : 0
  }

  /** Foundations → Structure read. bars ascending ({t,h,l,c,buy,sell}).
   *  Test low = lowest low of the last 5 min; prior low = lowest low of the
   *  30 → 5 min before. Held = test ≥ prior × 0.99, or the first close back
   *  above prior came ≤ 60s after the test low AND price is above it now.
   *  cvd = delta of the bars from the test-low bar to now. */
  _structureRead(price, bars, barMs) {
    const now = this._nowTape()
    const test = bars.filter(b => b.t >= now - 5 * 60_000)
    const prior = bars.filter(b => b.t >= now - 30 * 60_000 && b.t < now - 5 * 60_000)
    const out = { ok: false, why: 'PRIOR_LOW_NA', testLow: null, priorLow: null, reclaimSec: null, cvd: null }
    if (!test.length) return out
    let ti = 0
    for (let i = 1; i < test.length; i++) if (test[i].l < test[ti].l) ti = i
    const testLow = test[ti].l, tlT = test[ti].t
    let cvd = 0
    for (let i = ti; i < test.length; i++) cvd += test[i].buy - test[i].sell
    out.testLow = +testLow.toFixed(4); out.testLowT = tlT; out.cvd = +cvd.toFixed(3)
    // Need ≥ 10 min of prior structure to call a swing low.
    if (!prior.length || prior[0].t > now - 15 * 60_000) return out
    let priorLow = Infinity
    for (const b of prior) if (b.l < priorLow) priorLow = b.l
    out.priorLow = +priorLow.toFixed(4)
    let held = testLow >= priorLow * 0.99
    if (!held) {
      for (let j = ti; j < test.length; j++) {
        if (test[j].c > priorLow) { out.reclaimSec = Math.round((test[j].t + barMs - tlT) / 1000); break }
      }
      held = out.reclaimSec != null && out.reclaimSec <= 60 && price > priorLow
    }
    out.held = held
    if (!held) return { ...out, why: 'PRIOR_LOW_LOST' }
    if (!(cvd > 0)) return { ...out, why: 'NO_DELTA_UP' }
    return { ...out, ok: true, why: 'ok' }
  }

  /** Foundations → Absorption cells. Same cell rule as the chart's
   *  absorption cells: a level whose SELL volume ≥ mult × median cell volume
   *  (last 30 min) in a bar that CLOSED above it (score sell × (close − m) /
   *  range, best per bar). Needs one such bid cell in a bar that ended in
   *  the last ctxAbsorbSec, within max(0.5 ATR, 2%) of the test low, and
   *  price now ≥ cell + 0.25 ATR (loosened 2026-10-23). */
  _absorbRead(price, bars, barMs, testLow, atr) {
    const cfg = this.config
    const now = this._nowTape()
    const out = { ok: false, why: 'NO_ABSORB_CELL', cell: null }
    if (!(testLow > 0)) return out
    const vols = []
    for (const b of bars) if (b.t >= now - 30 * 60_000) for (const x of b.lv) vols.push(x[1] + x[2])
    if (vols.length < 8) return out
    vols.sort((a, b) => a - b)
    const median = vols[vols.length >> 1] || 0
    if (!(median > 0)) return out
    const minVol = median * (cfg.ctxAbsorbMult || 2)
    const tol = Math.max(atr > 0 ? 0.5 * atr : 0, testLow * 0.02)
    const from = now - (cfg.ctxAbsorbSec || 300) * 1000
    let best = null
    for (const b of bars) {
      if (b.t + barMs < from) continue
      const range = b.h - b.l
      if (!(range > 0)) continue
      let bb = null
      for (const [m, , se] of b.lv) {
        if (se >= minVol && b.c > m) {
          const sc = se * ((b.c - m) / range)
          if (!bb || sc > bb.sc) bb = { m, sell: se, sc, t: b.t }
        }
      }
      // Chart marks only the bar's BEST cell; it must be a bid cell near the low.
      if (bb && Math.abs(bb.m - testLow) <= tol && (!best || bb.sc > best.sc)) best = bb
    }
    if (!best) return out
    out.cell = { mcap: +best.m.toFixed(4), sell: +best.sell.toFixed(3), t: best.t, x: +(best.sell / median).toFixed(1) }
    const need = best.m + (atr > 0 ? 0.25 * atr : 0)   // reaction 0.25 ATR (bot-loosen-20261023)
    if (!(price >= need)) return { ...out, why: 'ABSORB_NO_REACTION' }
    return { ...out, ok: true, why: 'ok' }
  }

  /** Foundations → Trend: lower 5m highs or value (POC) migrating down. */
  _trendRead(src) {
    const cfg = this.config
    const now = this._nowTape()
    const out = { ok: false, why: 'TREND_NA', lowerHighs: null, pocNow: null, pocPrev: null }
    const b5 = this._mergeBars(src.filter(b => b.t >= now - 40 * 60_000), 300_000, true).slice(-6)
    if (b5.length < 6) return out
    let lh = 0
    for (let i = 1; i < b5.length; i++) if (b5[i].h < b5[i - 1].h) lh++
    out.lowerHighs = lh
    const recent = src.filter(b => b.t >= now - 30 * 60_000)
    const older = src.filter(b => b.t >= now - 60 * 60_000 && b.t < now - 30 * 60_000)
    const vaNow = recent.length ? this._valueArea(recent) : null
    // Prior half only counts with ≥ 10 min of bars in it.
    const vaPrev = older.length && older[0].t <= now - 40 * 60_000 ? this._valueArea(older) : null
    if (vaNow) out.pocNow = +vaNow.poc.toFixed(4)
    if (vaPrev) out.pocPrev = +vaPrev.poc.toFixed(4)
    const pocDown = !!(vaNow && vaPrev && vaNow.poc < vaPrev.poc * 0.98)
    out.pocDown = pocDown
    if (lh >= cfg.ctxTrendLH || pocDown) return { ...out, why: 'DOWNTREND' }
    return { ...out, ok: true, why: 'ok' }
  }

  /** Market section gate. ctx.market (bot_runner): { ts, sol1h, sol6h,
   *  breadth, n, err, coin: { symbol, name, themes, hits:[{k, others}] } }.
   *  priceChange values are PERCENT. → { pass, why, ctx }. */
  _marketGate(ctx) {
    const cfg = this.config
    if (!cfg.mktFilter && !cfg.mktTheme) return { pass: true, why: 'ok', ctx: null }
    const m = ctx.market
    const age = m && m.ts > 0 ? Math.round((Date.now() - m.ts) / 1000) : null
    const fresh = age != null && age <= cfg.mktMaxAgeSec
    const snap = { age, sol1h: m?.sol1h ?? null, sol6h: m?.sol6h ?? null, breadth: m?.breadth ?? null, err: m?.err || null }
    if (cfg.mktFilter) {
      if (!fresh || snap.sol1h == null || snap.sol6h == null || snap.breadth == null) return { pass: false, why: 'MARKET_NA', ctx: snap }
      if (snap.sol1h < cfg.mktSol1hMin || snap.sol6h < cfg.mktSol6hMin || snap.breadth < cfg.mktBreadthMin) {
        return { pass: false, why: 'MARKET_WEAK', ctx: snap }
      }
    }
    if (cfg.mktTheme) {
      const coin = m?.coin
      if (!fresh || !coin) return { pass: false, why: 'THEME_NA', ctx: snap }
      const hits = Array.isArray(coin.hits) ? coin.hits : []
      snap.coin = coin.symbol || null
      snap.theme = hits.slice(0, 3).map(h => `${String(h.k).replace(/^w:/, '')}:${h.others}`)
      if (!hits.some(h => h.others >= cfg.mktThemeMin)) return { pass: false, why: 'NO_HOT_THEME', ctx: snap }
    }
    return { pass: true, why: 'ok', ctx: snap }
  }

  /** Foundations gate after the location gate passes: Structure →
   *  Absorption cells → Trend. → { pass, why, ctx } (ctx = the reads, for
   *  the skip log + trade log). All toggles off = pass, nothing computed. */
  _contextGate(ctx) {
    const cfg = this.config
    const out = { pass: true, why: 'ok', ctx: {} }
    if (!cfg.ctxStructure && !cfg.ctxAbsorb && !cfg.ctxTrend) return out
    try {
      const now = this._nowTape()
      const price = ctx.highlightMcap
      const src = this._ctxBars(ctx.candles, now - 65 * 60_000)
      const fast = !!cfg.ctxFast
      const barMs = fast ? this._barMs(ctx.candles) : 60_000
      const sBars = fast ? src : this._mergeBars(src, 60_000, true)
      const atr = this._atrLast(this._mgmtBars(ctx.candles), cfg.atrPeriod || 5)
      out.ctx.fast = fast
      let st = null
      if (cfg.ctxStructure || cfg.ctxAbsorb) {
        st = this._structureRead(price, sBars, barMs)
        out.ctx.structure = { why: st.why, testLow: st.testLow, priorLow: st.priorLow, reclaimSec: st.reclaimSec, cvd: st.cvd }
        if (cfg.ctxStructure && !st.ok) return { ...out, pass: false, why: st.why }
      }
      if (cfg.ctxAbsorb) {
        const ab = this._absorbRead(price, sBars, barMs, st?.testLow, atr)
        out.ctx.absorb = { why: ab.why, cell: ab.cell }
        if (!ab.ok) return { ...out, pass: false, why: ab.why }
      }
      if (cfg.ctxTrend) {
        const tr = this._trendRead(src)
        out.ctx.trend = { why: tr.why, lowerHighs: tr.lowerHighs, pocNow: tr.pocNow, pocPrev: tr.pocPrev }
        if (!tr.ok) return { ...out, pass: false, why: tr.why }
      }
    } catch {
      return { ...out, pass: false, why: 'CTX_ERR' }
    }
    return out
  }


  // Entry-skip telemetry: near-miss log (rides status), per-base counters
  // (attached to the trade when it finally opens), and a bounded persisted
  // skip log (key solcheddar_bot_skips; the droplet merges it by id).
  static SKIP_KEY = 'solcheddar_bot_skips'
  static SKIP_MAX = 300
  _recordSkip(ctx, gate) {
    const b = this._activeBase
    if (b) {
      b.skips = b.skips || {}
      b.skips[gate.why] = (b.skips[gate.why] || 0) + 1
    }
    const now = Date.now()
    const last = this._lastSkip
    if (last && last.why === gate.why && last.base === b?.startTs && now - last.at < 30_000) return
    this._lastSkip = { why: gate.why, base: b?.startTs, at: now }
    this._logNearMiss(ctx, 'ENTRY_SKIP', { why: gate.why, gate })
    const row = {
      id: `${now}-${Math.random().toString(36).slice(2, 8)}`,
      time: now, token: ctx?.token || null, reason: gate.why,
      mcap: ctx?.highlightMcap || 0, mode: gate.mode,
      basePos: gate.basePos, extAtr: gate.extAtr, dz: gate.dz, upBars: gate.upBars,
      atr: gate.atr, vwap: gate.vwap,
      baseLow: b?.lowMcap || 0, baseHigh: b?.highMcap || 0,
      ctx: gate.ctx || null,
    }
    try {
      const raw = localStorage.getItem(OrderflowBot.SKIP_KEY)
      const arr = raw ? JSON.parse(raw) : []
      const list = Array.isArray(arr) ? arr : []
      list.push(row)
      localStorage.setItem(OrderflowBot.SKIP_KEY, JSON.stringify(list.slice(-OrderflowBot.SKIP_MAX)))
    } catch {}
  }
  getSkips() {
    try { const a = JSON.parse(localStorage.getItem(OrderflowBot.SKIP_KEY) || '[]'); return Array.isArray(a) ? [...a].reverse() : [] }
    catch { return [] }
  }

  // ============================================================
  // Initial stop + risk sizing
  // ============================================================
  /** → { stop, mode, dist, sizeSol, riskSol, skip? } for a LONG/SHORT entry at price. */
  _planEntry(ctx) {
    const cfg = this.config
    const price = ctx.highlightMcap
    const isLong = cfg.pilotDirection === 'LONG'
    const sgn = isLong ? 1 : -1
    const bars = this._mgmtBars(ctx.candles)
    const atr = this._atrLast(bars, cfg.atrPeriod || 5)
    const buf = Math.max(0, cfg.stopBufferPct || 0)
    const valid = (st) => st > 0 && (isLong ? st < price : st > price)
    const pct = price * (1 - sgn * (cfg.pilotStopPct || 0.04))
    const atrStop = atr > 0 ? price - sgn * (cfg.levelAtrMult || 2) * atr : 0
    let mode = cfg.pilotStopMode, stop = 0
    if (mode === 'base' && this._activeBase) {
      const ref = isLong ? this._activeBase.lowMcap : this._activeBase.highMcap
      stop = ref * (1 - sgn * buf)
    } else if (mode === 'swing') {
      const sw = isLong ? this._swingLowBelow(bars, price, cfg.swingStrength) : 0
      stop = sw > 0 ? sw * (1 - buf) : 0
    } else if (mode === 'atr') {
      stop = atrStop
    } else if (mode === 'pct') {
      stop = pct
    }
    if (!valid(stop)) {
      const fb = valid(atrStop) && mode !== 'atr' ? 'atr' : 'pct'
      mode = `${mode}→${fb}`
      stop = fb === 'atr' ? atrStop : pct
    }
    let dist = Math.abs(price - stop) / price
    const minD = Math.max(0, cfg.minStopPct || 0)
    if (dist < minD) { dist = minD; stop = price * (1 - sgn * minD) }
    const riskBudget = (cfg.starterSizeSol || 0) * (cfg.riskPct ?? 0.04)
    if (cfg.maxStopPct != null && dist > cfg.maxStopPct) {
      return { stop, mode, dist, sizeSol: 0, riskSol: 0, riskBudget, atr, skip: 'STOP_TOO_WIDE' }
    }
    const gap = this._gapReservePct(sgn)
    const riskDist = dist + gap
    let size = riskDist > 0 ? riskBudget / riskDist : cfg.starterSizeSol
    const lo = cfg.minSizeSol ?? 0, hi = cfg.maxSizeSol ?? Infinity
    size = Math.max(lo || 0, Math.min(hi == null ? Infinity : hi, size))
    if (!(size > 0)) size = cfg.starterSizeSol
    size = +size.toFixed(4)
    return { stop, mode, dist, gap, sizeSol: size, riskSol: size * riskDist, riskBudget, atr }
  }

  // ============================================================
  // Position shape + management
  // ============================================================
  _makePos(peakMcap, riskSolBudget) {
    return {
      peakMcap,
      atrStopMcap: 0,
      riskSolBudget,
      trailArmed: false,
      lockStopMcap: 0,     // profit-lock ladder stop (0 = not armed)
      lockR: null,         // lockR of the highest step reached
      lockStep: -1,
      printPeakMcap: 0,    // best print since entry (lock ladder reads it)
      peakScanT: this._lastPrintTime() || 0,   // prints after this are scanned
      scaled: false,
      remainFrac: 1,       // share of the position still open
      realizedSol: 0,      // SOL P&L already booked by scale-outs
      scaleOuts: [],
      runnerMode: false,
    }
  }

  /** Highest protective level now in force (initial stop, lock, armed trail). */
  _effStop() {
    const p = this._posRef()
    if (!p || !this._pos) return 0
    let st = p.stop || 0
    const better = (x) => x > 0 && (p.isLong ? x > st : (st === 0 || x < st))
    if (better(this._pos.lockStopMcap)) st = this._pos.lockStopMcap
    if (this._pos.trailArmed && better(this._pos.atrStopMcap)) st = this._pos.atrStopMcap
    return st
  }

  /** Lock-ladder stop breach → 'BE_LOCK' | 'LOCK_STEP' | null. Defers to the
   *  ATR trail when the trail is the tighter level (it reports ATR_TRAIL). */
  _lockBreach(highlightMcap) {
    const p = this._posRef()
    const pos = this._pos
    if (!p || !pos || !(pos.lockStopMcap > 0)) return null
    const lock = pos.lockStopMcap
    if (pos.trailArmed && pos.atrStopMcap > 0 && (p.isLong ? pos.atrStopMcap >= lock : pos.atrStopMcap <= lock)) return null
    const hit = p.isLong ? highlightMcap <= lock : highlightMcap >= lock
    if (!hit) return null
    return pos.lockR > 0 ? 'LOCK_STEP' : 'BE_LOCK'
  }

  /** SOL P&L of whatever is still open, marked at `mcap`. */
  _openLegsPnlSol(mcap, sideSign) {
    let pnl = 0
    const s0 = this._pilot?.sizeSol || 0, e0 = this._pilot?.mcap || 0
    const aL = this._load?.addSizeSol || 0, pL = this._load?.addMcap || 0
    if (s0 > 0 && e0 > 0) pnl += s0 * ((mcap - e0) * sideSign) / e0
    if (aL > 0 && pL > 0) pnl += aL * ((mcap - pL) * sideSign) / pL
    return pnl * (this._pos?.remainFrac ?? 1)
  }

  _fireScaleOut(ctx, currentR) {
    const pos = this._pos
    const frac = Math.min(1, Math.max(0, this.config.scaleOutPct || 0))
    if (!pos || !(frac > 0) || frac >= 1) return null
    const { highlightMcap } = ctx
    const isLong = (this._load?.direction || this.config.pilotDirection) === 'LONG'
    const sideSign = isLong ? 1 : -1
    const booked = this._openLegsPnlSol(highlightMcap, sideSign) * frac
    pos.realizedSol += booked
    pos.remainFrac = (pos.remainFrac ?? 1) * (1 - frac)
    pos.scaled = true
    const rSol = pos.riskSolBudget > 0 ? (pos.realizedSol - booked + booked / frac) / pos.riskSolBudget : currentR
    const ev = { time: Date.now(), mcap: highlightMcap, frac, r: +rSol.toFixed(3), r_mcap: +currentR.toFixed(3), pnlSol: +booked.toFixed(6) }
    pos.scaleOuts.push(ev)
    if (this._journalCurrent) {
      (this._journalCurrent.events = this._journalCurrent.events || []).push({ type: 'SCALE_OUT', ...ev })
    }
    const signal = {
      // PARTIAL_SELL is the vocabulary the chart executor already honours
      // (sellFraction of the remaining position); reason carries SCALE_OUT.
      type: 'PARTIAL_SELL',
      reason: 'SCALE_OUT',
      direction: isLong ? 'LONG' : 'SHORT',
      mcap: highlightMcap,
      sizePct: frac,
      level: 1,
      currentR: ev.r,
      currentR_mcap: currentR,
      time: Date.now(),
    }
    this.lastSignal = signal
    this.tradeLog.push(signal)
    this.onSignal?.(signal)
    return signal
  }

  /**
   * _managePosition(ctx) → exit reason string, or null.
   * Peak, profit-lock ladder, scale-out, ATR trail (armed after trailStartR),
   * runner giveback and the legacy PEAK_GIVEBACK — for ANY open position.
   */
  _managePosition(ctx) {
    const { highlightMcap } = ctx
    const p = this._posRef()
    const pos = this._pos
    if (!p || !pos || !(highlightMcap > 0)) return null
    const { basis, stop, isLong, risk } = p
    const cfg = this.config

    // ── High-water mark.
    if (isLong) { if (highlightMcap > pos.peakMcap) pos.peakMcap = highlightMcap }
    else        { if (highlightMcap < pos.peakMcap) pos.peakMcap = highlightMcap }
    const { peakR, currentR } = this._posR(highlightMcap)

    // ── Runner detection — sticky.
    if (!pos.runnerMode && peakR >= cfg.runnerActivateR) {
      pos.runnerMode = true
      if (this._journalCurrent) this._journalCurrent.runner = true
    }

    // ── 1. Profit-lock ladder. Only ratchets up.
    // Print-level peak (entry-gates2-20261018): the ladder reads the best
    // PRINT since entry, not just the 0.5s tick prices, so a +2R wick between
    // ticks still arms its step. Ladder only — trail/runner/giveback keep
    // the tick peak.
    const tape = this._rawSwaps
    if (tape && tape.length) {
      const from = pos.peakScanT || 0
      let j = tape.length - 1
      while (j >= 0 && (tape[j].time || 0) > from) j--
      for (let k = j + 1; k < tape.length; k++) {
        const px = this._printPx(tape[k])
        if (!(px > 0)) continue
        if (isLong) { if (px > (pos.printPeakMcap || 0)) pos.printPeakMcap = px }
        else if (!pos.printPeakMcap || px < pos.printPeakMcap) pos.printPeakMcap = px
      }
      pos.peakScanT = Math.max(from, tape[tape.length - 1].time || 0)
    }
    const ladderPeak = pos.printPeakMcap > 0
      ? (isLong ? Math.max(pos.peakMcap, pos.printPeakMcap) : Math.min(pos.peakMcap, pos.printPeakMcap))
      : pos.peakMcap
    const ladderPeakR = risk > 0 ? (ladderPeak - basis) * (isLong ? 1 : -1) / risk : peakR
    if (risk > 0 && Array.isArray(cfg.lockSteps) && cfg.lockSteps.length) {
      for (let i = cfg.lockSteps.length - 1; i > pos.lockStep; i--) {
        const [trigR, lockR] = cfg.lockSteps[i]
        if (!(ladderPeakR >= trigR)) continue
        const sgn = isLong ? 1 : -1
        const lvl = lockR > 0
          ? basis + sgn * lockR * risk
          : basis * (1 + sgn * Math.max(0, cfg.feeBuffer || 0))
        const tighter = isLong ? lvl > (pos.lockStopMcap || 0) : (pos.lockStopMcap === 0 || lvl < pos.lockStopMcap)
        if (!tighter) { pos.lockStep = i; break }   // an equal/higher lock already holds
        // A lock must sit on the protective side of price. If price has
        // already fallen back through this step's level, try the next LOWER
        // step instead of consuming this one (it can still arm later).
        if (isLong ? lvl < highlightMcap : lvl > highlightMcap) {
          pos.lockStopMcap = lvl
          pos.lockR = lockR
          pos.lockStep = i
          if (this._journalCurrent) {
            (this._journalCurrent.events = this._journalCurrent.events || []).push({
              type: lockR > 0 ? 'LOCK_STEP' : 'BE_LOCK', time: Date.now(), trigR, lockR, stop: lvl, peakR: +ladderPeakR.toFixed(3),
            })
          }
          break
        }
      }
    }

    // ── 2. Scale-out (once) at +scaleOutR.
    if (!pos.scaled && cfg.scaleOutR != null && cfg.scaleOutPct > 0 && currentR >= cfg.scaleOutR) {
      this._fireScaleOut(ctx, currentR)
    }

    // ── 3. ATR trail — the panel's period / HHV / mult, on mgmt bars. Arms
    //       only after peakR ≥ trailStartR; only ratchets; only counts once it
    //       is tighter than the initial stop.
    const loaded = !!this._load
    const period = loaded ? cfg.atrPeriod : cfg.pilotAtrPeriod
    const trailOn = cfg.trailEnabled && period != null && period > 0
    if (trailOn && !pos.trailArmed && (cfg.trailStartR == null || peakR >= cfg.trailStartR)) {
      pos.trailArmed = true
    }
    if (trailOn && pos.trailArmed) {
      const hhv = loaded ? cfg.atrHhvPeriod : cfg.pilotAtrHhvPeriod
      let mult = loaded ? cfg.loadAtrMult : cfg.pilotAtrMult
      if (pos.runnerMode && Number.isFinite(cfg.runnerAtrMult) && cfg.runnerAtrMult > 0) mult = cfg.runnerAtrMult
      const ts = this._atrTrailLast(this._mgmtBars(ctx.candles), period, hhv || 10, mult)
      if (ts > 0) {
        if (isLong) {
          if (ts < highlightMcap && ts > pos.atrStopMcap) pos.atrStopMcap = ts
        } else {
          const shortStop = basis + (basis - ts)
          if (shortStop > highlightMcap && (pos.atrStopMcap === 0 || shortStop < pos.atrStopMcap)) pos.atrStopMcap = shortStop
        }
      }
    }
    const floor = (() => {
      let f = stop
      if (pos.lockStopMcap > 0 && (isLong ? pos.lockStopMcap > f : pos.lockStopMcap < f)) f = pos.lockStopMcap
      return f
    })()
    const trailAbove = pos.trailArmed && pos.atrStopMcap > 0 && (isLong ? pos.atrStopMcap > floor : pos.atrStopMcap < floor)
    if (trailAbove) {
      const breached = isLong ? highlightMcap <= pos.atrStopMcap : highlightMcap >= pos.atrStopMcap
      if (breached) return 'ATR_TRAIL'
    }

    // ── 4. Runner giveback — protects a runner until the trail takes over.
    if (pos.runnerMode && !trailAbove && cfg.runnerGivebackPct != null && cfg.runnerGivebackPct > 0 && peakR > 0) {
      if (peakR - currentR >= peakR * cfg.runnerGivebackPct) return 'RUNNER_GIVEBACK'
    }

    // ── 5. Legacy giveback (panel "Giveback R"; default off).
    if (cfg.givebackEnabled && !pos.runnerMode && peakR > cfg.givebackAfterR) {
      if (peakR - currentR >= peakR * cfg.loadGivebackR) return 'PEAK_GIVEBACK'
    }
    return null
  }

  _onLoaded(ctx) {
    const { highlightMcap } = ctx
    if (!this._load) {
      this._transition(STATES.SCANNING)
      return null
    }
    const isLong = this._load.direction === 'LONG'
    const { basis, stop: initialStop } = this._posRef()

    // ── 0. Profit-lock ladder stop — above the initial stop once armed.
    const lockHit = this._lockBreach(highlightMcap)
    if (lockHit) return this._fireExit(ctx, lockHit)

    // ── 1. Initial / adopted stop breach — active from the moment we're loaded.
    //    Before this fix the initialStop was only used for R-math; nothing
    //    enforced it as a live stop, so a fast adverse move right after load
    //    fired (or after adoption) had no protection until peakR cleared 1R
    //    or ATR ratcheted in. Adoption especially needs this — the user
    //    handed over a position expecting the stop they specified to be live.
    if (initialStop > 0) {
      const initialBreached = isLong
        ? highlightMcap <= initialStop
        : highlightMcap >= initialStop
      if (initialBreached) {
        return this._fireExit(ctx, this._load.adopted ? 'ADOPTED_STOP' : 'INITIAL_STOP')
      }
    }

    // ── 2. POSITION MANAGEMENT — peak, trail, giveback.
    //    Was ~50 lines inlined right here, which is precisely why the pilot never
    //    got any of it. Same call the pilot makes now, same code, one copy.
    const managed = this._managePosition(ctx)
    if (managed) return this._fireExit(ctx, managed)

    // 3. Condition-degradation exit — the thesis-death check.
    //    Only fires for organic loads where the bot saw the imbalance fire
    //    and knows what thesis it's defending. Adopted positions keep just
    //    the price-based exits since the user's thesis is unknown to us.
    //
    //    Health = fraction of recent tape volume aligned with our direction.
    //    We tracked it dropping below LOAD_HEALTH_THRESHOLD; if it stays
    //    there for LOAD_HEALTH_PERSIST_MS, the auction has clearly turned
    //    against us and we exit BEFORE the price stop fires. Brief delta
    //    flips during normal consolidation won't trip this — they recover
    //    within the persistence window and reset the timer.
    //    RUNNER MODE: thesis-health stands down too — a 2.5R+ winner whose
    //    tape cools between legs is normal runner behavior; the ATR trail
    //    is the exit. (CONDITION_DEGRADED's -9.77R avg came from firing on
    //    losers late, not from protecting winners.)
    if (!this._load.adopted && !(this._pos && this._pos.runnerMode)) {
      const health = this._computeThesisHealth(this._load.direction)
      this._load.healthLast = health
      if (health < LOAD_HEALTH_THRESHOLD) {
        if (!this._load.degradedSince) this._load.degradedSince = Date.now()
        if (Date.now() - this._load.degradedSince >= LOAD_HEALTH_PERSIST_MS) {
          return this._fireExit(ctx, 'CONDITION_DEGRADED')
        }
      } else {
        // Health recovered above threshold — clear the degradation timer.
        // This is what protects us from premature exits during normal
        // consolidations: any recovery resets the clock.
        this._load.degradedSince = 0
      }
    }

    return null
  }

  // ============================================================
  // Per-print stops + gap reserve (bot-stops-20261017)
  // ============================================================
  _printPx(sw) { return +(sw?.tradeMcap ?? sw?.mcap ?? 0) || 0 }

  /** Time of the newest print in the tape buffer (0 if none). */
  _lastPrintTime() {
    const b = this._rawSwaps
    return b && b.length ? (b[b.length - 1].time || 0) : 0
  }

  /** p90 adverse per-print move over the last gapWindowSec, as a fraction,
   *  capped at gapReserveMaxPct. sideSign 1 = long (drops hurt). 0 when off
   *  or the tape is too thin to measure (< 10 moves). */
  _gapReservePct(sideSign = 1) {
    const cfg = this.config
    if (!cfg.gapReserve) return 0
    const sw = this._swapsInWindow(cfg.gapWindowSec || 60)
    const mv = []
    let prev = 0
    for (const s of sw) {
      const p = this._printPx(s)
      if (!(p > 0)) continue
      if (prev > 0) mv.push(Math.max(0, (prev - p) * sideSign / prev))
      prev = p
    }
    if (mv.length < 10) return 0
    mv.sort((a, b) => a - b)
    const g = mv[Math.floor(0.9 * (mv.length - 1))]
    return Math.min(Math.max(0, cfg.gapReserveMaxPct ?? 0.05), g || 0)
  }

  /** Hard-stop check against ONE print: lock ladder, initial/load stop, armed
   *  ATR trail. Exits at that print's price. No-op unless a position is open. */
  _printStopCheck(mcap) {
    if (!(mcap > 0) || !this._pos) return null
    if (this.state !== STATES.PILOT && this.state !== STATES.LOADED) return null
    const p = this._posRef()
    if (!p) return null
    const ctx = { highlightMcap: mcap }
    const lockHit = this._lockBreach(mcap)
    if (lockHit) return this._fireExit(ctx, lockHit)
    if (p.stop > 0 && (p.isLong ? mcap <= p.stop : mcap >= p.stop)) {
      const reason = this.state === STATES.LOADED
        ? (this._load?.adopted ? 'ADOPTED_STOP' : 'INITIAL_STOP')
        : 'PILOT_STOP'
      return this._fireExit(ctx, reason)
    }
    const pos = this._pos
    if (pos.trailArmed && pos.atrStopMcap > 0) {
      let floor = p.stop
      if (pos.lockStopMcap > 0 && (p.isLong ? pos.lockStopMcap > floor : pos.lockStopMcap < floor)) floor = pos.lockStopMcap
      const above = p.isLong ? pos.atrStopMcap > floor : pos.atrStopMcap < floor
      if (above && (p.isLong ? mcap <= pos.atrStopMcap : mcap >= pos.atrStopMcap)) return this._fireExit(ctx, 'ATR_TRAIL')
    }
    return null
  }

  /** Runner/browser hook: call on EVERY trade as it arrives. Stops then fire
   *  on the print itself instead of on the next tick's last price. */
  onPrint(mcap, time, token) {
    if (!this.enabled || !(mcap > 0)) return null
    if (token && this._currentToken && token !== this._currentToken) return null
    if (this.state !== STATES.PILOT && this.state !== STATES.LOADED) return null
    const t = Number(time) || 0
    if (t && t <= (this._stopScanT || 0)) return null
    if (t) this._stopScanT = t
    const sig = this._printStopCheck(mcap)
    if (sig) this._lastHighlightMcap = mcap
    return sig
  }

  /** Walk the tape buffer for prints newer than the last scan. */
  _scanPrintStops(ctx) {
    if (this.state !== STATES.PILOT && this.state !== STATES.LOADED) return null
    const buf = this._rawSwaps
    if (!buf || !buf.length) return null
    const from = this._stopScanT || 0
    let i = buf.length - 1
    while (i >= 0 && (buf[i].time || 0) > from) i--
    for (let j = i + 1; j < buf.length; j++) {
      const t = buf[j].time || 0
      this._stopScanT = Math.max(this._stopScanT || 0, t)
      const sig = this._printStopCheck(this._printPx(buf[j]))
      if (sig) return sig
    }
    return null
  }

  // ============================================================
  // Liquidity reaction speed — DISPLAY ONLY (bot-stops-20261017)
  // ------------------------------------------------------------
  // Levels: the high and low of the previous 10 closed mgmt bars (1m) —
  //   the nearest swing liquidity (resting stops sit just beyond them).
  // Sweep: a print trades beyond a level (below the low / above the high).
  // Reaction: seconds from that sweep print until price is back inside by
  //   0.5 × ATR(5) of the mgmt bars (low: ≥ level + 0.5 ATR; high: ≤ level
  //   − 0.5 ATR). No reclaim within 120 s = HOLD (liquidity taken, the move
  //   continued). A side re-arms once price trades back inside the range.
  // Status: last { side, sec | null (hold) }, median sec of the last 8
  //   reclaims, holds among the last 8 events.
  // ============================================================
  _updateLiqReaction(ctx) {
    try {
      const L = this._liq || (this._liq = { ev: null, lastT: 0, armLo: true, armHi: true, hist: [], last: null })
      const bars = this._mgmtBars(ctx.candles)
      if (bars.length < 7) return
      const prior = bars.slice(-10)
      let lo = Infinity, hi = 0
      for (const b of prior) { if (b.l < lo) lo = b.l; if (b.h > hi) hi = b.h }
      const atr = this._atrLast(bars, 5)
      if (!(atr > 0) || !(lo > 0) || !(hi > lo)) return
      const buf = this._rawSwaps
      if (!buf || !buf.length) return
      let i = buf.length - 1
      while (i >= 0 && (buf[i].time || 0) > L.lastT) i--
      const k = 0.5 * atr
      for (let j = i + 1; j < buf.length; j++) {
        const t = buf[j].time || 0
        const px = this._printPx(buf[j])
        L.lastT = Math.max(L.lastT, t)
        if (!(px > 0)) continue
        const ev = L.ev
        if (ev) {
          const back = ev.side === 'low' ? px >= ev.level + k : px <= ev.level - k
          if (back) {
            const sec = Math.max(0, (t - ev.t0) / 1000)
            this._liqRecord({ side: ev.side, sec, at: t })
            // Live hook (longpress-liq-20261017): one call per completed
            // reclaim. Swept low reclaimed = 'up', swept high = 'down'.
            // `at` is the reclaim print's tape time, `now` the tape clock
            // of this tick. Optional; the droplet runner never sets it.
            if (typeof this.onLiqReaction === 'function') {
              try {
                this.onLiqReaction({ dir: ev.side === 'low' ? 'up' : 'down', sec, at: t, now: Number.isFinite(ctx.now) ? ctx.now : null, token: ctx.token || null })
              } catch { /* listener errors never touch the bot */ }
            }
          } else if (t - ev.t0 > 120_000) {
            this._liqRecord({ side: ev.side, sec: null, at: t })
          }
          continue
        }
        if (px > lo && px < hi) { L.armLo = true; L.armHi = true; continue }
        if (px <= lo && L.armLo) { L.ev = { side: 'low', level: lo, t0: t }; L.armLo = false }
        else if (px >= hi && L.armHi) { L.ev = { side: 'high', level: hi, t0: t }; L.armHi = false }
      }
    } catch { /* display only — never let it break the bot */ }
  }

  _liqRecord(r) {
    const L = this._liq
    L.ev = null
    L.last = r
    L.hist.push(r)
    if (L.hist.length > 8) L.hist.shift()
  }

  _liqReactionStatus() {
    const L = this._liq
    if (!L || (!L.last && !L.ev)) return null
    const secs = L.hist.filter(h => h.sec != null).map(h => h.sec).sort((a, b) => a - b)
    return {
      last: L.last ? { side: L.last.side, sec: L.last.sec == null ? null : +L.last.sec.toFixed(1), at: L.last.at } : null,
      medianSec: secs.length ? +secs[Math.floor((secs.length - 1) / 2)].toFixed(1) : null,
      n: L.hist.length,
      holds: L.hist.filter(h => h.sec == null).length,
      pending: L.ev ? { side: L.ev.side, level: L.ev.level, t0: L.ev.t0 } : null,
    }
  }

  _onCooldown(_ctx) {
    if (Date.now() >= this._cooldownUntil) {
      this._transition(STATES.SCANNING)
    }
    return null
  }

  // ============================================================
  // Load trigger — has the imbalance fired?
  // ============================================================

  // Primary entry trigger: rising stacked sell-imbalance lows (LONG) /
  // falling stacked buy-imbalance highs (SHORT). Reads structure off completed
  // candles' mcapLevels — the same footprint the chart renders. Wicks are
  // ignored by construction (no volume = no imbalance). Drop-in replacement:
  // still returns { direction, ... } | null and still honors pilotDirection.
  _checkLoadTrigger(ctx) {
    if (!this._activeBase) return null
    const candles = ctx.candles
    if (!candles || candles.length < IMB_LOOKBACK + 2) return null

    const dir  = this.config.pilotDirection
    const side = dir === 'SHORT' ? 'buy' : 'sell'   // LONG structure = sell-imbalance lows

    // Completed candles only (exclude the forming last one), live-built only.
    const completed = candles.slice(0, -1).filter(c => c && !c.isHistorical)
    if (completed.length < IMB_LOOKBACK) return null
    const window = completed.slice(-IMB_LOOKBACK)

    // Per-candle structural extreme; skip candles with no qualifying stack.
    const extremes = []
    for (const c of window) {
      const e = imbalanceStackExtreme(c, side)
      if (e != null) extremes.push(e)
    }
    if (extremes.length < IMB_RISING_MIN) return null

    // Most recent IMB_RISING_MIN must strictly progress with the trend:
    //   LONG  → sell-imbalance lows strictly RISING
    //   SHORT → buy-imbalance highs strictly FALLING
    const seq = extremes.slice(-IMB_RISING_MIN)
    const progressing = dir === 'SHORT'
      ? seq.every((v, i) => i === 0 || v < seq[i - 1])
      : seq.every((v, i) => i === 0 || v > seq[i - 1])
    if (!progressing) return null

    // Structure intact: price still on the right side of the latest imbalance.
    const latest = seq[seq.length - 1]
    const { highlightMcap } = ctx
    const intact = dir === 'SHORT' ? highlightMcap < latest : highlightMcap > latest
    if (!intact) return null

    // Directional read of the most recent completed candle (journal/diagnostics).
    let buy = 0, sell = 0
    for (const lvl of Object.values(window[window.length - 1]?.mcapLevels || {})) {
      buy += lvl.buy || 0; sell += lvl.sell || 0
    }
    const tot = buy + sell
    const delta = tot > 0 ? (buy - sell) / tot : 0

    return {
      direction: dir === 'SHORT' ? 'SHORT' : 'LONG',
      imbLow: latest,
      lows: seq.slice(),
      risingCandles: IMB_RISING_MIN,
      delta,
    }
  }

  /**
   * Thesis-health score for the LOADED state. Returns 0..1 where:
   *   1.0 = recent tape volume is entirely aligned with our direction
   *   0.5 = balanced (no signal either way; insufficient data also returns this)
   *   0.0 = recent tape volume is entirely opposed
   *
   * The condition-exit fires when this drops below LOAD_HEALTH_THRESHOLD and
   * stays there for LOAD_HEALTH_PERSIST_MS. We deliberately do NOT combine
   * with churn here — churn measures two-sidedness, but a unilateral
   * against-us flow has LOW churn (no flips, no balance), which would
   * paradoxically read as "healthy" if averaged in.
   */
  _computeThesisHealth(direction) {
    const swaps = this._swapsInWindow(LOAD_HEALTH_WINDOW_SEC)
    // Not enough tape to read direction — return neutral so we don't
    // accidentally trip the degradation timer on a quiet patch.
    if (swaps.length < 5) return 0.5

    let buy = 0, sell = 0
    for (const sw of swaps) {
      const a = sw.amount || 0
      if (sw.side === 'buy') buy += a
      else if (sw.side === 'sell') sell += a
    }
    const totalVol = buy + sell
    if (totalVol <= 0) return 0.5

    const delta = (buy - sell) / totalVol             // -1..+1
    const aligned = direction === 'LONG' ? delta : -delta
    return (aligned + 1) / 2                          // 0..1
  }

  /**
   * Load liquidity/volatility gate — the ARD Discord filter, applied to real
   * size. Builds a volume-by-price profile from the raw tape window and
   * requires: (1) enough swaps to read at all, (2) real range in the window,
   * (3) current price sitting within LOAD_NODE_BAND_PCT of a top-loadTopNodes
   * volume node. Returns { pass, rangePct, valueRank, valueStrength, why }.
   */
  _loadValueGate(highlightMcap) {
    const cfg = this.config
    const out = { pass: false, rangePct: 0, valueRank: 0, valueStrength: 0, why: '' }
    if (!cfg.loadGateEnabled) return { ...out, pass: true, why: 'gate off' }
    if (!(highlightMcap > 0)) return { ...out, why: 'no price' }

    const swaps = this._swapsInWindow(LOAD_GATE_WINDOW_SEC)
    if (swaps.length < cfg.loadGateMinSwaps) {
      return { ...out, why: `thin tape (${swaps.length}/${cfg.loadGateMinSwaps} swaps in ${LOAD_GATE_WINDOW_SEC}s)` }
    }

    // Volatility — traded-price range over the window.
    let lo = Infinity, hi = -Infinity
    for (const sw of swaps) {
      const m = sw.tradeMcap ?? sw.mcap ?? 0
      if (m > 0) { if (m < lo) lo = m; if (m > hi) hi = m }
    }
    if (!(hi > lo)) return { ...out, why: 'no price range in window' }
    const rangePct = ((hi - lo) / ((hi + lo) / 2)) * 100
    out.rangePct = rangePct
    if (rangePct < cfg.loadMinRangePct) {
      return { ...out, why: `flat book (${rangePct.toFixed(1)}% < ${cfg.loadMinRangePct}% range)` }
    }

    // Volume-by-price profile → top nodes by volume.
    const step = Math.max(1e-9, highlightMcap * LOAD_NODE_STEP_PCT)
    const bins = new Map()
    for (const sw of swaps) {
      const m = sw.tradeMcap ?? sw.mcap ?? 0
      const a = sw.amount || 0
      if (!(m > 0) || !(a > 0)) continue
      const key = Math.round(m / step)
      bins.set(key, (bins.get(key) || 0) + a)
    }
    if (!bins.size) return { ...out, why: 'no volume profile' }
    const nodes = [...bins.entries()]
      .map(([k, vol]) => ({ price: k * step, vol }))
      .sort((a, b) => b.vol - a.vol)
    const take = Math.max(1, Math.round(nodes.length * LOAD_NODE_TOP_FRAC))
    const topVol = nodes[0].vol
    const band = highlightMcap * LOAD_NODE_BAND_PCT
    for (let i = 0; i < take; i++) {
      if (Math.abs(nodes[i].price - highlightMcap) <= band) {
        out.valueRank = i + 1
        out.valueStrength = topVol > 0 ? nodes[i].vol / topVol : 0
        break
      }
    }
    if (!out.valueRank) return { ...out, why: 'price not at a value node' }
    if (out.valueRank > cfg.loadTopNodes) {
      return { ...out, why: `node rank ${out.valueRank} > top ${cfg.loadTopNodes}` }
    }
    return { ...out, pass: true, why: 'ok' }
  }

  // ============================================================
  // Signal firing
  // ============================================================

  _firePilot(ctx) {
    const { highlightMcap } = ctx
    // Initial stop by mode + risk-based size (same SOL risk every trade).
    const plan = this._planEntry(ctx)
    if (plan.skip) {
      const gate = { pass: false, why: plan.skip,
        stopMode: plan.mode, stopDistPct: +(plan.dist * 100).toFixed(2) }
      this._entryGateLast = { ...(this._entryGateLast || {}), ...gate, at: Date.now() }
      this._recordSkip(ctx, gate)
      return null
    }
    const stopMcap = plan.stop
    const sizeSol = plan.sizeSol
    const timeCapAt = this.config.pilotHoldMode === 'off'
      ? 0
      : Date.now() + this.config.pilotMaxHoldMin * 60_000
    const gateRead = this._entryGateLast || null
    const baseSkips = this._activeBase?.skips ? { ...this._activeBase.skips } : {}

    this._pilot = {
      mcap: highlightMcap,
      sizeSol,
      openedAt: Date.now(),
      stopMcap,
      timeCapAt,
    }
    // The POSITION, as distinct from the leg. Peak, trail, lock ladder and
    // scale-out state live here so they survive the pilot→load transition.
    // riskSolBudget = size × stop distance — the SOL value of 1R.
    this._pos = this._makePos(highlightMcap, plan.riskSol)
    this._stopScanT = this._lastPrintTime() || this._nowTape()   // per-print stop scan starts AFTER the entry print
    this._transition(STATES.PILOT)

    // Journal start
    this._journalCurrent = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      token: ctx?.token || null,
      pilotDirection: this.config.pilotDirection,
      openedAt: Date.now(),
      pilotEntry: {
        mcap: highlightMcap,
        sizeSol,
        stopMcap,
        stopMode: plan.mode,
        stopDistPct: +(plan.dist * 100).toFixed(2),
        riskSol: +plan.riskSol.toFixed(6),
        gapPct: +((plan.gap || 0) * 100).toFixed(2),
        riskBudgetSol: +plan.riskBudget.toFixed(6),
        atr: +(plan.atr || 0).toFixed(4),
        timeCapAt,
        gate: gateRead ? {
          why: gateRead.why, basePos: gateRead.basePos, extAtr: gateRead.extAtr,
          dz: gateRead.dz, upBars: gateRead.upBars, vwap: gateRead.vwap,
          ctx: gateRead.ctx || null,   // Foundations reads (liq/value/structure/absorb/trend)
          skipsBefore: baseSkips,
        } : null,
        baseSnapshot: {
          durationMs: this._activeBase.durationMs,
          lowMcap: this._activeBase.lowMcap,
          highMcap: this._activeBase.highMcap,
          peakChurn: this._activeBase.peakChurn,
          totalVol: this._activeBase.totalVol,
          absorption: this._baseAbsorption(),
        },
        ...this._snapshotMetrics(ctx),
      },
      load: null,
      exit: null,
    }

    const signal = {
      type: 'PILOT_OPEN',
      direction: this.config.pilotDirection,
      mcap: highlightMcap,
      sizeSol,
      stop: stopMcap,
      reason: `Pilot — base mature (${Math.round(this._activeBase.durationMs/1000)}s, `
            + `peak churn ${this._activeBase.peakChurn.toFixed(1)}, `
            + `absorption ${this._baseAbsorption().toFixed(1)}x)`
            + ` · gate ${gateRead?.why || 'off'} · stop ${plan.mode} ${(plan.dist * 100).toFixed(1)}%`
            + ` · ${sizeSol} SOL`,
      time: Date.now(),
    }
    this.lastSignal = signal
    this.tradeLog.push(signal)
    this._dailyTradeCount++
    this.onSignal?.(signal)
    return signal
  }

  _fireLoad(ctx, trigger) {
    const { highlightMcap } = ctx
    const starterSize = this._pilot?.sizeSol || 0
    const starterMcap = this._pilot?.mcap || highlightMcap
    const configuredAdd = this.config.loadSizeSol
    // Provisional blended basis at the CONFIGURED size — used only by the
    // pct-fallback stop below (rarely fires; the structural stop is primary).
    // The real basis is recomputed after risk sizing; they differ by <1%.
    const provisionalTotal = starterSize + configuredAdd
    const blendedBasis0 = provisionalTotal > 0
      ? (starterMcap * starterSize + highlightMcap * configuredAdd) / provisionalTotal
      : highlightMcap

    // ── The load carries ITS OWN stop. ──
    // Without this, _posRef() fell back to the PILOT's stop (pilotStopPct
    // below the PILOT entry) for a loaded position. The load fires AFTER
    // strength, at a higher price, with ~6x the pilot's size — so 6/7 of
    // the position was protected by a stop that could sit 10-50% below its
    // own entry. INITIAL_STOP averaging -8R in the journal was exactly that
    // ride. The trigger hands us the structural floor (imbLow: the latest
    // rising sell-imbalance stack low) — the thesis is invalid beyond it,
    // so the stop belongs just past it. Fallback: pilotStopPct off the
    // blended basis. Never looser than the pilot's original stop.
    const isLongLoad = trigger.direction !== 'SHORT'
    const pctStop = isLongLoad
      ? blendedBasis0 * (1 - this.config.pilotStopPct)
      : blendedBasis0 * (1 + this.config.pilotStopPct)
    const structStop = trigger.imbLow > 0
      ? (isLongLoad ? trigger.imbLow * (1 - LOAD_STOP_STRUCT_BUFFER)
                    : trigger.imbLow * (1 + LOAD_STOP_STRUCT_BUFFER))
      : 0
    // The structural stop must sit on the protective side of the current
    // price (the trigger's intact-check guarantees price is beyond imbLow,
    // but the buffer could cross a very tight gap — guard anyway).
    let loadStop = structStop
    if (!(loadStop > 0) || (isLongLoad ? loadStop >= highlightMcap : loadStop <= highlightMcap)) {
      loadStop = pctStop
    }
    const pilotStopRef = this._pilot?.stopMcap || 0
    if (pilotStopRef > 0) {
      loadStop = isLongLoad ? Math.max(loadStop, pilotStopRef) : Math.min(loadStop, pilotStopRef)
    }

    // ── RISK-NEUTRAL SIZING ──
    // The stop is structural (the thesis is invalid beyond it) — SIZE is the
    // free variable. Total SOL at risk after the load, each leg losing its
    // own % to the stop, may not exceed loadRiskCapR × the pilot's SOL risk
    // budget. Journal (67 trades): loads won +1.64R and lost -8.6R
    // size-weighted — the payoff needs an 84% win rate to break even. This
    // caps a failed load at what a failed pilot costs, so the load's job
    // goes back to being selection, not leverage.
    const dirSign = isLongLoad ? 1 : -1
    const pilotRiskSol = (starterSize > 0 && starterMcap > 0 && pilotStopRef > 0)
      ? starterSize * Math.abs(starterMcap - pilotStopRef) / starterMcap
      : starterSize * this.config.pilotStopPct
    // The pilot's planned SOL risk (incl. its gap reserve) when known.
    const budgetSol = (this._pos?.riskSolBudget || pilotRiskSol) * (this.config.loadRiskCapR ?? 1)
    // Pilot leg's risk to the (raised) load stop — always ≤ its original risk.
    const pilotLegRiskSol = (starterSize > 0 && starterMcap > 0)
      ? starterSize * Math.max(0, (starterMcap - loadStop) * dirSign) / starterMcap
      : 0
    // Risk per SOL added at the current price against the load stop.
    const perSolRisk = highlightMcap > 0
      ? Math.max(0, (highlightMcap - loadStop) * dirSign) / highlightMcap
      : 1
    // Gap reserve: both legs can gap past the (tight, structural) load stop —
    // the log's INITIAL_STOPs did -2.5R/-3.3R on stops 0.5–0.9% under basis.
    const loadGap = this._gapReservePct(dirSign)
    const remainingSol = budgetSol - pilotLegRiskSol - starterSize * loadGap
    const perSolRiskG = perSolRisk + loadGap
    const affordableSol = perSolRiskG > 0 ? Math.max(0, remainingSol / perSolRiskG) : configuredAdd
    const addSize = Math.min(configuredAdd, affordableSol)
    // Dust guard: if the structure is too far away to afford a meaningful
    // add inside the budget, skip the load entirely — stay pilot.
    const minAdd = Math.max(0.01, configuredAdd * 0.1)
    if (addSize < minAdd) {
      const now = Date.now()
      if (!this._lastRiskSkipAt || now - this._lastRiskSkipAt > 30_000) {
        this._lastRiskSkipAt = now
        if (this._nearMissLog.length >= 100) this._nearMissLog.shift()
        this._nearMissLog.push({
          type: 'LOAD_RISK_SKIP', time: now, mcap: highlightMcap, stop: loadStop,
          affordableSol: +affordableSol.toFixed(4), budgetSol: +budgetSol.toFixed(4),
          why: `stop ${(perSolRisk * 100).toFixed(1)}% away — structure too far for budget`,
        })
      }
      return null
    }
    const totalSizeFinal = starterSize + addSize
    const blendedBasis = totalSizeFinal > 0
      ? (starterMcap * starterSize + highlightMcap * addSize) / totalSizeFinal
      : highlightMcap
    const riskAtLoadSol = pilotLegRiskSol + addSize * perSolRisk + (starterSize + addSize) * loadGap

    this._load = {
      addMcap: highlightMcap,
      addSizeSol: addSize,
      addedAt: Date.now(),
      direction: trigger.direction,
      blendedBasis,
      stopMcap: loadStop,   // the load's OWN stop — read by _posRef()
      // peakMcap/peakR/atrStopMcap deliberately GONE from here. They live on
      // this._pos, which was created when the pilot fired and carries the peak
      // the pilot already made — loading is adding size, not starting over.
      // Condition-degradation tracking (organic loads only — see _onLoaded)
      degradedSince: 0,
      healthLast: 0.5,
    }
    this._transition(STATES.LOADED)

    if (this._journalCurrent) {
      this._journalCurrent.load = {
        addMcap: highlightMcap,
        addSizeSol: addSize,
        addSizeConfigured: configuredAdd,          // risk sizing visible in journal
        riskBudgetSol: +budgetSol.toFixed(5),
        riskAtLoadSol: +riskAtLoadSol.toFixed(5),
        gapPct: +(loadGap * 100).toFixed(2),
        blendedBasis,
        direction: trigger.direction,
        addedAt: Date.now(),
        stopMcap: loadStop,
        triggerDelta: trigger.delta,
        triggerImbLow: trigger.imbLow,
        triggerRising: trigger.risingCandles,
        gate: trigger.gate ? {                     // load liquidity gate readings
          rangePct: +(trigger.gate.rangePct || 0).toFixed(2),
          valueRank: trigger.gate.valueRank || 0,
          valueStrength: +(trigger.gate.valueStrength || 0).toFixed(3),
        } : null,
        ...this._snapshotMetrics(ctx),
      }
    }

    const signal = {
      type: 'LOAD_ADD',
      direction: trigger.direction,
      mcap: highlightMcap,
      addSizeSol: addSize,
      blendedBasis,
      stop: loadStop,
      reason: `Load — ${trigger.risingCandles} ${trigger.direction === 'SHORT' ? 'falling buy' : 'rising sell'}-imbalance `
            + `${trigger.direction === 'SHORT' ? 'highs' : 'lows'}, floor ${Math.round(trigger.imbLow)}`
            + (addSize < configuredAdd ? ` · risk-scaled ${addSize.toFixed(3)}/${configuredAdd.toFixed(2)} SOL` : '')
            + (trigger.gate ? ` · node #${trigger.gate.valueRank} · vol ${(trigger.gate.rangePct || 0).toFixed(1)}%` : ''),
      time: Date.now(),
    }
    this.lastSignal = signal
    this.tradeLog.push(signal)
    this.onSignal?.(signal)
    return signal
  }

  _fireExit(ctx, reason) {
    // Double-trigger guard: a per-print stop and the tick can both see the
    // same breach; only the first one with an open position may exit.
    if (!this._pilot && !this._load) return null
    const { highlightMcap } = ctx
    const basis = this._load?.blendedBasis || this._pilot?.mcap || highlightMcap
    const isLong = (this._load?.direction || this.config.pilotDirection) === 'LONG'
    const sideSign = isLong ? 1 : -1
    // Position-shape risk, read BEFORE the refs are cleared below. This is
    // the R denominator: basis-to-stop of the position AS IT ACTUALLY
    // EXISTS (pilot: entry→pilot stop, identical to the old math; loaded:
    // blendedBasis→load stop).
    const pRef = this._posRef()
    const pnlPct = basis > 0 ? (highlightMcap - basis) * sideSign / basis : 0

    this._dailyPnL += pnlPct
    this._cooldownUntil = Date.now() + this.config.cooldownMs

    const totalSize = (this._pilot?.sizeSol || 0) + (this._load?.addSizeSol || 0)
    const signal = {
      type: 'EXIT',
      direction: this._load?.direction || this.config.pilotDirection,
      mcap: highlightMcap,
      basis,
      pnlPct,
      sizeSol: totalSize * (this._pos?.remainFrac ?? 1),
      remainFrac: this._pos?.remainFrac ?? 1,
      reason,
      time: Date.now(),
    }

    // Finalize journal entry
    if (this._journalCurrent) {
      const j = this._journalCurrent
      // Organic trades carry pilotEntry; adopted trades carry adoption.
      // Either gives us the reference entry mcap + stop for R-multiple math.
      const entryRef = j.pilotEntry || j.adoption || null
      const refMcap = entryRef?.mcap || basis
      const refStop = entryRef?.stopMcap || 0
      // Legacy entry-anchored R: |pilot entry − pilot stop| as denominator.
      // On loaded trades this measured the loss from the (much higher,
      // load-weighted) blended basis against the PILOT's tiny risk unit —
      // the -31.79R "trade" was largely this artifact, not a real 32-unit
      // loss. Kept as pnlR_entryAnchored so old journal rows stay comparable.
      const initialRisk = (refMcap > 0 && refStop > 0)
        ? Math.abs(refMcap - refStop)
        : 0
      const legacyR = initialRisk > 0
        ? (highlightMcap - basis) * sideSign / initialRisk
        : 0
      const positionRisk = (pRef && pRef.risk > 0) ? pRef.risk : 0
      const finalR = positionRisk > 0
        ? (highlightMcap - basis) * sideSign / positionRisk
        : legacyR
      // ── Size-aware (SOL-denominated) R ──
      // exit.pnlR is per-UNIT: a loaded stop at the load's own stop books
      // ≈ -1R there even when the wallet lost 7× the pilot's SOL. Per-leg
      // SOL P&L against the pilot's SOL budget is the dollar truth the
      // expectancy dashboard needs — with risk-neutral loading these two
      // series converge; the gap between them is exactly the size risk.
      // Scale-outs already booked part of it; the rest is marked here.
      const realizedSol = this._pos?.realizedSol || 0
      const pnlSol = realizedSol + this._openLegsPnlSol(highlightMcap, sideSign)
      const riskBudgetSol = this._pos?.riskSolBudget || 0
      const pnlRSol = riskBudgetSol > 0 ? pnlSol / riskBudgetSol : null
      const peakRSol = riskBudgetSol > 0 ? Math.max(this._solR(highlightMcap).peakR, pnlRSol) : null
      const peakRMcap = this._posR(highlightMcap).peakR
      j.exit = {
        pnlSol: +pnlSol.toFixed(6),
        riskBudgetSol: +riskBudgetSol.toFixed(6),
        pnlR_sol: riskBudgetSol > 0 ? +(pnlSol / riskBudgetSol).toFixed(3) : 0,
        mcap: highlightMcap,
        basis,
        reason,
        pnlPct,
        // pnlR / peakR = SOL-based (pnlSol / original riskBudgetSol). The old
        // basis→stop numbers inflate after a load; kept as *_mcap for debug.
        pnlR: pnlRSol != null ? +pnlRSol.toFixed(3) : finalR,
        pnlR_mcap: finalR,
        pnlR_entryAnchored: legacyR,
        riskMcap: positionRisk || initialRisk,
        stopMcap: pRef?.stop || refStop || 0,
        totalSizeSol: totalSize,
        loadFired: !!j.load,
        // Was `this._load?.peakR || 0` — which is why 16/18 journal entries
        // reported peakR 0.00, four of them while closing GREEN. It wasn't a
        // broken counter: on a pilot-only trade this._load simply doesn't exist,
        // so the journal asked the wrong object and got exactly what it asked
        // for. Now it comes off the POSITION and is derived from a real
        // high-water mark, so it can't be below the realised R again.
        peakR: peakRSol != null ? +peakRSol.toFixed(3) : peakRMcap,
        peakR_mcap: peakRMcap,
        peakMcap: this._pos?.peakMcap || 0,
        // Management state at exit.
        lockStopMcap: this._pos?.lockStopMcap || 0,
        lockR: this._pos?.lockR ?? null,
        trailArmed: !!this._pos?.trailArmed,
        atrStopMcap: this._pos?.atrStopMcap || 0,
        scaleOuts: this._pos?.scaleOuts?.length ? this._pos.scaleOuts.slice() : [],
        realizedSol: +realizedSol.toFixed(6),
        remainFrac: this._pos?.remainFrac ?? 1,
        ...this._snapshotMetrics(ctx),
      }
      j.closedAt = Date.now()
      j.durationMs = j.closedAt - j.openedAt
      this._journal.push(j)
      this._journalCurrent = null
      this._saveJournal()
    }

    this._transition(STATES.COOLDOWN)
    this._pilot = null
    this._load = null
    this._pos = null
    this._activeBase = null   // require a NEW base after exit

    this.lastSignal = signal
    this.tradeLog.push(signal)
    this.onSignal?.(signal)
    return signal
  }

  // ============================================================
  // Manual position adoption — auto-pilot
  // ============================================================

  /**
   * Take over management of a position the user opened manually.
   * Skips the qualification funnel entirely — drops straight into LOADED
   * with full management (initial stop check, ATR trail, peakR giveback).
   *
   * Returns { ok, reason?, signal? }. Reasons for refusal:
   *   BOT_DISABLED   — bot is off; enable it first
   *   ALREADY_ACTIVE — bot is already managing a trade (PILOT or LOADED)
   *   INVALID_INPUT  — missing/invalid mcap or sizeSol
   *
   * Inputs:
   *   mcap         — entry market cap (required)
   *   sizeSol      — position size in SOL (required)
   *   direction    — 'LONG' or 'SHORT' (default: config.pilotDirection)
   *   stopMcap     — explicit stop level (default: entry ± pilotStopPct)
   *   peakMcap     — if you've already seen a higher high than entry, pass
   *                  it so peakR math reflects reality (default: entry)
   */
  adoptPosition({ mcap, sizeSol, direction, stopMcap, peakMcap } = {}) {
    if (!this.enabled) return { ok: false, reason: 'BOT_DISABLED' }
    if (this.state !== STATES.SCANNING && this.state !== STATES.COOLDOWN) {
      return { ok: false, reason: 'ALREADY_ACTIVE' }
    }
    if (!mcap || mcap <= 0 || !sizeSol || sizeSol <= 0) {
      return { ok: false, reason: 'INVALID_INPUT' }
    }

    const dir = direction || this.config.pilotDirection || 'LONG'
    const isLong = dir === 'LONG'

    // Protective stop is based on the CURRENT price, not the entry. When you
    // hand off a position the intent is "protect it from here" — if we based
    // the stop on a possibly-old entry, adopting an already-underwater
    // position would trip the stop on the very next tick and exit instantly.
    // The entry (mcap) is still used as blendedBasis so P/L stays accurate.
    const refPrice = this._lastHighlightMcap > 0 ? this._lastHighlightMcap : mcap
    const computedStop = isLong
      ? refPrice * (1 - this.config.pilotStopPct)
      : refPrice * (1 + this.config.pilotStopPct)
    const effectiveStop = (stopMcap && stopMcap > 0) ? stopMcap : computedStop

    // No pilot — adopted positions skip that step. Set _load directly with
    // the adopted basis and stop. _onLoaded's initialStop fallback will
    // pick up adoptedStopMcap. peakMcap seeds from the current price so the
    // peakR/giveback logic measures from where the bot took over.
    this._pilot = null
    // Adoption skips the pilot entirely, so _pos is born here instead. Without
    // this, _managePosition() sees no _pos and an adopted position gets no peak,
    // no trail and no giveback — the exact hole the pilot was in.
    // Adopted budget: the adopted size against its effective stop from the
    // takeover price — same denominator convention as the organic pilot.
    this._pos = this._makePos(
      peakMcap || refPrice,
      refPrice > 0 ? (sizeSol || 0) * Math.abs(refPrice - effectiveStop) / refPrice : 0,
    )

    this._load = {
      addMcap: mcap,
      addSizeSol: sizeSol,
      addedAt: Date.now(),
      direction: dir,
      blendedBasis: mcap,
      adopted: true,
      adoptedStopMcap: effectiveStop,
      // Shape consistency only — condition exit is gated on !adopted, so
      // these are never read for adopted positions.
      degradedSince: 0,
      healthLast: 0.5,
    }

    // Cancel any active cooldown (user is consciously overriding)
    this._cooldownUntil = 0
    this._stopScanT = this._lastPrintTime() || this._nowTape()   // per-print stops start AFTER adoption
    this._transition(STATES.LOADED)

    // Open a journal entry tagged as adopted
    this._journalCurrent = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      token: null,
      pilotDirection: dir,
      openedAt: Date.now(),
      adopted: true,
      adoption: {
        mcap,
        sizeSol,
        direction: dir,
        stopMcap: effectiveStop,
        peakMcap: peakMcap || mcap,
        ...this._snapshotMetrics({ highlightMcap: mcap }),
      },
      pilotEntry: null,
      load: null,
      exit: null,
    }

    const signal = {
      type: 'ADOPT',
      direction: dir,
      mcap,
      sizeSol,
      stop: effectiveStop,
      reason: `Auto-pilot: adopted manual ${dir.toLowerCase()} at ${mcap.toFixed(0)} (stop ${effectiveStop.toFixed(0)})`,
      time: Date.now(),
    }
    this.lastSignal = signal
    this.tradeLog.push(signal)
    this._dailyTradeCount++
    this.onSignal?.(signal)

    return { ok: true, signal }
  }

  /**
   * Release the bot from managing the current position WITHOUT firing exit.
   * "I'll take it from here." The in-progress journal entry is discarded
   * rather than recorded as a real trade — release isn't a trade outcome,
   * it's a handoff back to manual.
   *
   * Returns { ok, reason? }. Only works from PILOT or LOADED.
   */
  releasePosition() {
    if (this.state !== STATES.LOADED && this.state !== STATES.PILOT) {
      return { ok: false, reason: 'NOT_ACTIVE' }
    }
    this._pilot = null
    this._load = null
    this._pos = null
    this._activeBase = null
    // Discard the in-progress journal entry — not a completed trade
    this._journalCurrent = null
    this._transition(STATES.SCANNING)

    const signal = {
      type: 'RELEASE',
      reason: 'Auto-pilot released — manual takeover',
      time: Date.now(),
    }
    this.lastSignal = signal
    this.tradeLog.push(signal)
    this.onSignal?.(signal)
    return { ok: true, signal }
  }

  _logNearMiss(ctx, missReason, extras = {}) {
    if (this._nearMissLog.length >= 100) this._nearMissLog.shift()
    this._nearMissLog.push({
      time: Date.now(),
      token: ctx?.token || null,
      missReason,
      mcap: ctx?.highlightMcap || 0,
      base: this._activeBase ? {
        durationMs: this._activeBase.durationMs,
        peakChurn: this._activeBase.peakChurn,
        absorption: this._baseAbsorption(),
      } : null,
      ...this._snapshotMetrics(ctx),
      ...extras,
    })
  }

  // ============================================================
  // Plumbing
  // ============================================================

  _transition(newState) {
    if (this.state === newState) return
    const prev = this.state
    this.state = newState
    this.stateLog.push({ from: prev, to: newState, time: Date.now() })
    if (this.stateLog.length > 100) this.stateLog.shift()
    this.onStateChange?.({ from: prev, to: newState })
  }

  _resetDailyIfNeeded() {
    const now = Date.now()
    const dayMs = 24 * 60 * 60 * 1000
    if (now - this._dayStart > dayMs) {
      this._dayStart = now
      this._dailyPnL = 0
      this._dailyTradeCount = 0
    }
  }

  /**
   * Clear all token-specific state. Called when the chart navigates to a
   * different token. Any open position must already have been force-exited
   * by the caller in update() — this just wipes the structural state that
   * doesn't transfer (base, reactivity history, position refs).
   *
   * Preserved (cross-token session state): _dailyPnL, _dailyTradeCount,
   * _cooldownUntil, _journal, _nearMissLog, config, enabled flag.
   */
  _resetTokenState() {
    this._activeBase = null
    this._pilot = null
    this._load = null
    this._pos = null
    this._loadGateLast = null
    this._reactivityHistory = []
    this._lastReactivityClosedTime = 0
    this._reactivityNow = 0
    this._reactivityAvg = 0
    this._stopScanT = 0
    this._liq = null
    this._sv = null      // session VWAP accumulator (Foundations → VWAP)
    this._liqVa = null   // liquidity window read (Foundations → Liquidity/Value)
    // Tape buffer ref and DLMM bins get reassigned on the next update()
    // tick from the new ctx; metrics (churn, tape heat) recompute fresh.
  }

  // ============================================================
  // Tape buffer helpers
  // ============================================================

  _nowTape() { return this._tapeNow ?? Date.now() }

  _swapsInWindow(seconds) {
    if (!this._rawSwaps || !this._rawSwaps.length || !(seconds > 0)) return []
    const cutoff = this._nowTape() - seconds * 1000
    const buf = this._rawSwaps
    let i = buf.length - 1
    while (i >= 0 && (buf[i].time || 0) >= cutoff) i--
    return buf.slice(i + 1)
  }

  // ============================================================
  // DLMM (Meteora) bin liquidity reads
  // ============================================================

  _nextWallAbove(fromMcap, minSol = 5) {
    if (!this._meteoraBins.length || !(fromMcap > 0)) return null
    for (const bin of this._meteoraBins) {
      if (bin.mcap <= fromMcap) continue
      if (bin.notionalSol >= minSol) {
        const distancePct = (bin.mcap - fromMcap) / fromMcap
        return { mcap: bin.mcap, notionalSol: bin.notionalSol, distancePct }
      }
    }
    return null
  }

  _nextWallBelow(fromMcap, minSol = 5) {
    if (!this._meteoraBins.length || !(fromMcap > 0)) return null
    for (let i = this._meteoraBins.length - 1; i >= 0; i--) {
      const bin = this._meteoraBins[i]
      if (bin.mcap >= fromMcap) continue
      if (bin.notionalSol >= minSol) {
        const distancePct = (fromMcap - bin.mcap) / fromMcap
        return { mcap: bin.mcap, notionalSol: bin.notionalSol, distancePct }
      }
    }
    return null
  }

  // ============================================================
  // Microstructure metrics
  // ============================================================

  _computeTapeHeat(candles, timeIntervalSec) {
    if (!candles || candles.length < 6) return 0
    const live = candles[candles.length - 1]
    if (!live || live.isHistorical) return 0
    const liveVol = this._candleVolume(live)
    let elapsedFrac = 1.0
    if (timeIntervalSec > 0 && live.startTime) {
      const elapsedMs = Date.now() - live.startTime
      const intervalMs = timeIntervalSec * 1000
      elapsedFrac = Math.min(1.0, Math.max(0.05, elapsedMs / intervalMs))
    }
    const liveProjected = liveVol / elapsedFrac
    let sum = 0, n = 0
    for (let i = candles.length - 6; i < candles.length - 1; i++) {
      const c = candles[i]
      if (!c || c.isHistorical) continue
      sum += this._candleVolume(c)
      n++
    }
    if (n === 0) return 0
    const avg = sum / n
    return avg > 0 ? liveProjected / avg : 0
  }

  _computeChurnScore(candles, timeIntervalSec) {
    // Reads RAW TAPE via _swapsInWindow (closed candles' mcapLevels are
    // packed/cleared before bot.update runs — only the tape has fresh data).
    const windowSec   = Math.max(2, this.config.churnTapeWindowSec   ?? 12)
    const baselineSec = Math.max(windowSec * 4, this.config.churnTapeBaselineSec ?? 300)

    const win = this._swapsInWindow(windowSec)
    if (win.length < 8) return 0

    const base = this._swapsInWindow(baselineSec)
    if (base.length < win.length) return 0

    let winBuy = 0, winSell = 0, flips = 0, prevSide = null
    let minM = Infinity, maxM = -Infinity
    for (const sw of win) {
      const a = sw.amount || 0
      if (sw.side === 'buy')       winBuy  += a
      else if (sw.side === 'sell') winSell += a
      const m = sw.tradeMcap || 0
      if (m > 0) {
        if (m < minM) minM = m
        if (m > maxM) maxM = m
      }
      if (prevSide !== null && sw.side !== prevSide) flips++
      prevSide = sw.side
    }
    const winVol = winBuy + winSell
    if (winVol <= 0) return 0

    let baseVol = 0, blMin = Infinity, blMax = -Infinity
    for (const sw of base) {
      baseVol += (sw.amount || 0)
      const m = sw.tradeMcap || 0
      if (m > 0) {
        if (m < blMin) blMin = m
        if (m > blMax) blMax = m
      }
    }
    if (baseVol <= 0) return 0

    const winRate  = winVol  / windowSec
    const baseRate = baseVol / baselineSec
    const volRatio = baseRate > 0 ? winRate / baseRate : 0
    const volZ     = Math.max(0, Math.min(1, (volRatio - 0.4) / 1.1))

    const balance = 1 - Math.abs(winBuy - winSell) / winVol

    const flipRate = win.length > 1 ? flips / (win.length - 1) : 0
    const flip     = Math.max(0, Math.min(1, flipRate / 0.5))

    const refMcap       = win[win.length - 1].tradeMcap || maxM || 1
    const winRange      = (maxM > 0 && minM < Infinity) ? maxM - minM : 0
    const winTravelPct  = Math.max(0.001, refMcap > 0 ? winRange / refMcap : 1)
    const winAbsorption = winRate / winTravelPct

    const blRef        = base[base.length - 1].tradeMcap || blMax || refMcap
    const blRange      = (blMax > 0 && blMin < Infinity) ? blMax - blMin : 0
    const blTravelPct  = Math.max(0.001, blRef > 0 ? blRange / blRef : 1)
    const blAbsorption = baseRate / blTravelPct

    const pinRatio = blAbsorption > 0 ? winAbsorption / blAbsorption : 0
    const pinning  = Math.max(0, Math.min(1, (pinRatio - 0.4) / 1.1))

    const components = [volZ, balance, flip, pinning]
    const avg = (volZ + balance + flip + pinning) / 4
    const minComp = Math.min(...components)
    const gate = minComp < 0.2 ? minComp / 0.2 : 1
    return Math.max(0, Math.min(10, avg * gate * 10))
  }

  _computeReactivity(candles, timeIntervalSec) {
    if (!candles || candles.length < 8) return
    const live = candles[candles.length - 1]
    if (!live || live.isHistorical) return

    let baselineSum = 0, baselineN = 0
    for (let i = candles.length - 8; i < candles.length - 1; i++) {
      const c = candles[i]
      if (!c || c.isHistorical) continue
      baselineSum += this._candleVolume(c)
      baselineN++
    }
    const baselineAvg = baselineN > 0 ? baselineSum / baselineN : 0

    let elapsedFrac = 1.0
    if (timeIntervalSec > 0 && live.startTime) {
      const elapsedMs = Date.now() - live.startTime
      elapsedFrac = Math.min(1.0, Math.max(0.05, elapsedMs / (timeIntervalSec * 1000)))
    }
    this._reactivityNow = this._computeReactivityForBar(live, baselineAvg, elapsedFrac)

    const lastClosed = candles[candles.length - 2]
    if (lastClosed && !lastClosed.isHistorical && lastClosed.startTime !== this._lastReactivityClosedTime) {
      const r = this._computeReactivityForBar(lastClosed, baselineAvg, 1.0)
      this._reactivityHistory.push(r)
      if (this._reactivityHistory.length > 10) this._reactivityHistory.shift()
      this._lastReactivityClosedTime = lastClosed.startTime
    }
    this._reactivityAvg = this._reactivityHistory.length > 0
      ? this._reactivityHistory.reduce((a, b) => a + b, 0) / this._reactivityHistory.length
      : 0
  }

  _computeReactivityForBar(c, baselineAvg, elapsedFrac) {
    const vol = this._candleVolume(c)
    const delta = this._candleDelta(c)
    if (vol <= 0 || baselineAvg <= 0) return 0
    const volProj = vol / elapsedFrac
    const volRatio = volProj / baselineAvg
    const range = (c.high - c.low) || 0
    const displacement = (c.close - c.open) || 0
    if (range <= 0) return 0
    const directionRatio = displacement / range
    const deltaSign = Math.sign(delta)
    const dispSign = Math.sign(displacement)
    const aligned = (deltaSign === dispSign && deltaSign !== 0) ? 1 : -1
    return aligned * Math.min(1, volRatio) * Math.abs(directionRatio)
  }

  // ============================================================
  // Candle helpers
  // ============================================================

  _candleDelta(candle) {
    if (!candle || candle.isHistorical) return 0
    let delta = 0
    for (const lvl of Object.values(candle.mcapLevels || {})) {
      delta += (lvl.buy || 0) - (lvl.sell || 0)
    }
    return delta
  }

  _candleVolume(candle) {
    if (!candle || candle.isHistorical) return 0
    let vol = 0
    for (const lvl of Object.values(candle.mcapLevels || {})) {
      vol += (lvl.buy || 0) + (lvl.sell || 0)
    }
    return vol
  }
}

// ============================================================
// drawBotOverlay — chart canvas overlay
// ============================================================

export function drawBotOverlay(ctx, bot, layout) {
  if (!bot.enabled) return
  const { plotWidth, totalHeight, sortedMcaps } = layout
  if (!sortedMcaps || sortedMcaps.length < 2) return
  const topMcap = sortedMcaps[0]
  const botMcap = sortedMcaps[sortedMcaps.length - 1]
  const mcapRange = topMcap - botMcap
  // The chart's own continuous map when passed (row-edge, log-aware) — the
  // local linear fallback disagreed with candles and cells.
  const mcapToY = typeof layout.mcapToY === 'function'
    ? layout.mcapToY
    : (mcap) => mcapRange > 0 ? ((topMcap - mcap) / mcapRange) * totalHeight : totalHeight / 2

  const status = bot.getStatus()

  // ─── Base range overlay (faint, while base is tracking) ───
  if (status.activeBase) {
    const yHigh = mcapToY(status.activeBase.highMcap)
    const yLow  = mcapToY(status.activeBase.lowMcap)
    const yTop = Math.max(0, Math.min(yHigh, yLow))
    const yBot = Math.min(totalHeight, Math.max(yHigh, yLow))
    if (yBot > yTop) {
      ctx.save()
      ctx.fillStyle = status.activeBase.qualified ? '#448aff22' : '#88888816'
      ctx.fillRect(0, yTop, plotWidth, yBot - yTop)
      ctx.strokeStyle = status.activeBase.qualified ? '#448aff66' : '#88888844'
      ctx.lineWidth = 1
      ctx.setLineDash([2, 4])
      ctx.beginPath(); ctx.moveTo(0, yTop); ctx.lineTo(plotWidth, yTop); ctx.stroke()
      ctx.beginPath(); ctx.moveTo(0, yBot); ctx.lineTo(plotWidth, yBot); ctx.stroke()
      ctx.restore()
    }
  }

  if (status.state !== STATES.PILOT && status.state !== STATES.LOADED) return

  ctx.save()

  // Pilot entry line
  if (status.pilot && status.pilot.mcap > 0) {
    const y = mcapToY(status.pilot.mcap)
    if (y >= 0 && y <= totalHeight) {
      ctx.strokeStyle = '#ffb300'
      ctx.lineWidth = 1
      ctx.setLineDash([6, 4])
      ctx.globalAlpha = 0.7
      ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(plotWidth, y); ctx.stroke()
      ctx.setLineDash([])
      ctx.font = '500 10px "JetBrains Mono", monospace'
      ctx.fillStyle = '#ffb300'
      ctx.globalAlpha = 0.9
      ctx.textBaseline = 'bottom'
      ctx.fillText('PILOT', plotWidth - 46, y - 2)
    }
  }

  // Load (blended basis) line — only when LOADED
  if (status.state === STATES.LOADED && status.load) {
    const y = mcapToY(status.load.blendedBasis)
    if (y >= 0 && y <= totalHeight) {
      ctx.strokeStyle = '#00c853'
      ctx.lineWidth = 1
      ctx.setLineDash([6, 4])
      ctx.globalAlpha = 0.8
      ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(plotWidth, y); ctx.stroke()
      ctx.setLineDash([])
      ctx.font = '500 10px "JetBrains Mono", monospace'
      ctx.fillStyle = '#00c853'
      ctx.globalAlpha = 0.9
      ctx.textBaseline = 'bottom'
      ctx.fillText('BASIS', plotWidth - 46, y - 2)
    }
  }

  // Stop line — pilot stop while PILOT, ATR trail while LOADED
  // Effective stop: the highest of initial stop, lock ladder and armed trail.
  const stopMcap = status.position?.effStopMcap
    || (status.state === STATES.LOADED
      ? (status.load?.atrStopMcap || status.load?.stopMcap || status.pilot?.stopMcap || 0)
      : (status.pilot?.stopMcap || 0))
  if (stopMcap > 0) {
    const y = mcapToY(stopMcap)
    if (y >= 0 && y <= totalHeight) {
      ctx.strokeStyle = '#ff1744'
      ctx.lineWidth = 1
      ctx.setLineDash([4, 4])
      ctx.globalAlpha = 0.7
      ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(plotWidth, y); ctx.stroke()
      ctx.setLineDash([])
      ctx.font = '500 10px "JetBrains Mono", monospace'
      ctx.fillStyle = '#ff1744'
      ctx.globalAlpha = 0.9
      ctx.textBaseline = 'top'
      ctx.fillText('STOP', plotWidth - 46, y + 2)
    }
  }

  ctx.restore()
}
